import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Search,
  Send,
  ChevronLeft,
  ChevronRight,
  Image,
  Smile,
  Hash,
  Copy,
  Calendar,
  Settings,
  Bell,
  Database,
  AlertTriangle,
  Pencil,
  Eye,
  FileText,
  MessageSquare,
  Target,
  Users,
  Globe2,
  Clock3,
  ShieldCheck,
  Lightbulb,
  Sparkles,
  RotateCcw,
  Save,
  Bold,
  Italic,
  Link2,
  List,
  CheckCircle2,
  Tag,
  X,
  type LucideIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { useExclusiveSelect } from '../../hooks/useExclusiveSelect';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  readSharedScheduleRange,
  readSharedScheduledPosts,
  removeSharedScheduledPost,
  upsertSharedScheduledPost,
  writeSharedScheduleRange,
} from './linkedinScheduleStorage';
import {
  LanguageFlagByCode,
  LANGUAGE_OPTIONS,
  type LanguageCode,
} from '../../components/LanguageFlag';
import {
  createDefaultCampaignStrategy,
  formatStrategySavedLabel,
  generateStrategyAiPreview,
  getStrategyCategoryLabel,
  getStrategyTimeZoneLabel,
  hasStrategyValidationErrors,
  readCampaignStrategySnapshot,
  strategiesAreEqual,
  LANGUAGE_TIME_ZONES,
  STRATEGY_LIMITS,
  STRATEGY_TIME_ZONE_ORDER,
  validateCampaignStrategy,
  writeCampaignStrategySnapshot,
  type CampaignStrategyData,
  type StrategyAiPreview,
  type StrategyCategoryId,
  type StrategyFieldKey,
  type StrategyTimeZoneId,
} from './campaignStrategyStorage';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

/* ── Types ── */
type LinkedInStatus = 'Active' | 'Draft' | 'Paused' | 'Completed' | 'Scheduled';
type PostStatus = 'Approved' | 'Draft' | 'Scheduled' | 'Posted';

interface LinkedInPost {
  id: number;
  title: string;
  date: string;
  status: PostStatus;
  preview: string;
  content: string;
  hashtags: string;
}

interface LinkedInCampaign {
  id: number;
  name: string;
  type: string;
  status: LinkedInStatus;
  language: string;
  createdAt: string;
  posts: LinkedInPost[];
}

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  text: string;
}

/* ── Mock Data ── */
const MOCK_CAMPAIGNS: LinkedInCampaign[] = [
  {
    id: 1,
    name: 'Brand Awareness Q2',
    type: 'Awareness',
    status: 'Active',
    language: 'English (EN)',
    createdAt: 'May 12, 2025',
    posts: [
      { id: 1, title: 'Introducing Our New Feature', date: 'May 20, 2025', status: 'Approved', preview: 'Excited to share our latest updates...', content: 'Excited to share our latest updates! 🚀\n\nOur new feature is designed to help you work smarter, not harder. Check out what\'s new and let us know what you think!', hashtags: '#Innovation #ProductUpdate #Growth', },
      { id: 2, title: 'Behind the Scenes', date: 'May 22, 2025', status: 'Draft', preview: 'Behind every great product...', content: 'Behind every great product is an amazing team. Here\'s a look at how we build and innovate every day.', hashtags: '#TeamWork #BehindTheScenes', },
      { id: 3, title: 'Did You Know?', date: 'May 24, 2025', status: 'Draft', preview: 'Did you know? Our platform...', content: 'Did you know? Our platform processes over 1 million data points daily to bring you the most relevant insights.', hashtags: '#DataDriven #Innovation', },
      { id: 4, title: 'Upcoming Webinar', date: 'May 27, 2025', status: 'Scheduled', preview: 'Join us for an exclusive webinar...', content: 'Join us for an exclusive webinar on digital transformation strategies for 2025. Register now!', hashtags: '#Webinar #DigitalTransformation', },
    ],
  },
  { id: 2, name: 'Product Launch 2025', type: 'Product Launch', status: 'Active', language: 'English (EN)', createdAt: 'Apr 15, 2025', posts: [] },
  { id: 3, name: 'Customer Success Stories', type: 'Engagement', status: 'Draft', language: 'English (EN)', createdAt: 'May 1, 2025', posts: [] },
  { id: 4, name: 'Thought Leadership Series', type: 'Content', status: 'Active', language: 'English (EN)', createdAt: 'Mar 20, 2025', posts: [] },
  { id: 5, name: 'Events Promotion', type: 'Event', status: 'Paused', language: 'English (EN)', createdAt: 'Feb 10, 2025', posts: [] },
  { id: 6, name: 'Recruitment Drive', type: 'Recruitment', status: 'Draft', language: 'English (EN)', createdAt: 'May 5, 2025', posts: [] },
  { id: 7, name: 'Industry Insights', type: 'Thought Leadership', status: 'Completed', language: 'English (EN)', createdAt: 'Jan 15, 2025', posts: [] },
];

const MOCK_CHAT: ChatMessage[] = [
  { id: 1, role: 'assistant', text: "Hi! I'm your AI assistant. What would you like to write about today?" },
  { id: 2, role: 'user', text: 'Give me a quick campaign overview summary' },
  { id: 3, role: 'assistant', text: "Here's a quick overview of your campaign:\n\n- 28 total posts\n- 64% completion rate\n- Next post tomorrow at 09:00 AM\n- 45,231 impressions so far\n- 3.12% engagement rate\n\nYour campaign is on track.\n\nWhat would you like to do next?" },
];

const STATUS_STYLE: Record<LinkedInStatus, string> = {
  Active: 'bg-green-100 text-green-700 border-green-200',
  Draft: 'bg-blue-100 text-blue-700 border-blue-200',
  Paused: 'bg-red-100 text-red-700 border-red-200',
  Completed: 'bg-slate-100 text-slate-600 border-slate-200',
  Scheduled: 'bg-amber-100 text-amber-700 border-amber-200',
};

const POST_STATUS_STYLE: Record<PostStatus, string> = {
  Approved: 'border-green-200/80 bg-green-50 text-green-700',
  Draft: 'border-blue-200/80 bg-blue-50 text-blue-700',
  Scheduled: 'border-amber-200/80 bg-amber-50 text-amber-700',
  Posted: 'border-slate-200/80 bg-slate-50 text-slate-600',
};

const POST_HEADER_BADGE_STYLE: Record<PostStatus, string> = {
  Approved: 'bg-green-500 text-white',
  Draft: 'bg-blue-500 text-white',
  Scheduled: 'bg-amber-500 text-white',
  Posted: 'bg-slate-500 text-white',
};

const TABS = ['Overview', 'Content', 'Strategy', 'Schedule', 'Analytics', 'Settings'] as const;

type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

const TAB_I18N: Record<(typeof TABS)[number], string> = {
  Overview: 'campaigns.tab.overview',
  Content: 'campaigns.tab.content',
  Strategy: 'campaigns.tab.strategy',
  Schedule: 'campaigns.tab.schedule',
  Analytics: 'campaigns.tab.analytics',
  Settings: 'campaigns.tab.settings',
};

function translateTab(tab: (typeof TABS)[number], t: TranslateFn): string {
  return t(TAB_I18N[tab]);
}

function translatePostStatus(status: PostStatus, t: TranslateFn): string {
  return t(`linkedInSchedule.status.${status.toLowerCase()}`);
}

function translateLinkedInStatus(status: LinkedInStatus, t: TranslateFn): string {
  const keyByStatus: Record<LinkedInStatus, string> = {
    Active: 'campaigns.status.active',
    Draft: 'linkedInSchedule.status.draft',
    Paused: 'common.paused',
    Scheduled: 'linkedInSchedule.status.scheduled',
    Completed: 'campaigns.status.completed',
  };
  return t(keyByStatus[status]);
}

const CAMPAIGN_TYPE_I18N: Record<string, string> = {
  Awareness: 'campaigns.type.awareness',
  'Product Launch': 'campaigns.type.productLaunch',
  Engagement: 'campaigns.type.engagement',
  Content: 'campaigns.type.content',
  Event: 'campaigns.type.event',
  Recruitment: 'campaigns.type.recruitment',
  'Thought Leadership': 'campaigns.type.thoughtLeadership',
};

function translateCampaignType(type: string, t: TranslateFn): string {
  const key = CAMPAIGN_TYPE_I18N[type];
  return key ? t(key) : type;
}

function formatCampaignDisplayDate(value: string, localeTag: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(localeTag, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatOverviewDateTime(date: Date, localeTag: string): string {
  return date.toLocaleString(localeTag, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const SCHEDULE_VIEW_MODE_I18N = {
  Week: 'linkedInSchedule.viewWeek',
  Month: 'linkedInSchedule.viewMonth',
  List: 'linkedInSchedule.viewList',
} as const;

const CAMPAIGN_SURFACE_BG = 'bg-[rgb(233_241_255)]';
const CAMPAIGN_SCROLLBAR = 'hawk-scrollbar-rainbow';
const SELECTED_POST_CARD =
  'border-blue-300/90 bg-gradient-to-br from-white via-white/95 to-sky-50/90 ring-1 ring-blue-200/80 backdrop-blur-xl';
const IDLE_POST_CARD =
  'border-white/50 bg-white/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] backdrop-blur-md hover:border-blue-200/55 hover:bg-white/60 hover:shadow-[0_8px_24px_-14px_rgba(59,130,246,0.22)]';

const GLASS_FRAME =
  `border border-white/50 shadow-[0_8px_32px_rgba(15,23,42,0.08)] rounded-2xl ${CAMPAIGN_SURFACE_BG}`;
const LIQUID_GLASS_PANEL =
  `border border-white/55 shadow-[0_18px_60px_-32px_rgba(30,64,175,0.45)] ring-1 ring-white/35 ${CAMPAIGN_SURFACE_BG}`;
const LIQUID_GLASS_CARD =
  `border border-white/55 shadow-[0_16px_44px_-30px_rgba(15,23,42,0.42)] ring-1 ring-white/45 ${CAMPAIGN_SURFACE_BG}`;
const LIQUID_GLASS_SOFT =
  `border border-white/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] ${CAMPAIGN_SURFACE_BG}`;

const SCHEDULE_WINDOW_DAYS = 7;

type CalendarPostStatus = 'approved' | 'scheduled' | 'draft' | 'posted';

type CalendarDay = {
  date: Date;
  key: string;
  weekday: string;
  dayNum: number;
  isToday: boolean;
};

type CalendarPost = {
  label: string;
  time: string;
  status: CalendarPostStatus;
  check?: boolean;
};

const CALENDAR_POSTS: Record<string, CalendarPost[]> = {
  '2025-05-19': [{ label: 'Post 2, 3', time: '10:00 AM', status: 'approved', check: true }],
  '2025-05-22': [{ label: 'Post 0', time: '10:00 AM', status: 'scheduled', check: true }],
  '2025-05-23': [{ label: 'Post 3, 5', time: '10:00 AM', status: 'approved', check: true }],
  '2025-05-27': [{ label: 'Post 4, 1', time: '10:00 AM', status: 'draft' }],
};

const DEFAULT_SCHEDULE_TIME = '10:00 AM';

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateKey(key: string): Date | null {
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = startOfDay(new Date(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfMonth(date: Date): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
}

function addMonths(date: Date, months: number): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth() + months, 1));
}

function compareDays(a: Date, b: Date): number {
  return startOfDay(a).getTime() - startOfDay(b).getTime();
}

function formatPickerDate(date: Date, localeTag: string): string {
  return date.toLocaleDateString(localeTag, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function buildCalendarDays(windowStart: Date, localeTag: string): CalendarDay[] {
  const today = startOfDay(new Date());
  const start = startOfDay(windowStart);
  return Array.from({ length: SCHEDULE_WINDOW_DAYS }, (_, i) => {
    const date = addDays(start, i);
    return {
      date,
      key: toDateKey(date),
      weekday: date.toLocaleDateString(localeTag, { weekday: 'short' }),
      dayNum: date.getDate(),
      isToday: isSameDay(date, today),
    };
  });
}

function formatScheduleRange(days: CalendarDay[], localeTag: string): string {
  if (days.length === 0) return '';
  const first = days[0].date;
  const last = days[days.length - 1].date;
  return formatDateRangeLabel(first, last, localeTag);
}

function formatDateRangeLabel(first: Date, last: Date, localeTag: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' };
  if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
    return `${first.toLocaleDateString(localeTag, opts)} – ${last.toLocaleDateString(localeTag, yearOpts)}`;
  }
  return `${first.toLocaleDateString(localeTag, yearOpts)} – ${last.toLocaleDateString(localeTag, yearOpts)}`;
}

function getInitialCampaignScheduleRange(): { start: Date; end: Date } {
  const sharedRange = readSharedScheduleRange();
  const sharedStart = sharedRange ? parseDateKey(sharedRange.startKey) : null;
  const sharedEnd = sharedRange ? parseDateKey(sharedRange.endKey) : null;
  if (sharedStart && sharedEnd) return { start: sharedStart, end: sharedEnd };

  const start = getDefaultScheduleWindowStart();
  return { start, end: addDays(start, SCHEDULE_WINDOW_DAYS - 1) };
}

function getInitialScheduleWindowStart(): Date {
  return getInitialCampaignScheduleRange().start;
}

function getInitialScheduleWindowEnd(): Date {
  return getInitialCampaignScheduleRange().end;
}

function getDefaultScheduleWindowStart(): Date {
  const today = startOfDay(new Date());
  return addDays(today, -3);
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildCalendarCells(monthDate: Date): Array<Date | null> {
  const monthStart = startOfMonth(monthDate);
  const firstDayOffset = monthStart.getDay();
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const cells: Array<Date | null> = Array.from({ length: firstDayOffset }, () => null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfDay(new Date(monthStart.getFullYear(), monthStart.getMonth(), day)));
  }

  return cells;
}

const CALENDAR_CARD_STYLE: Record<CalendarPostStatus, string> = {
  approved: 'border-blue-200 bg-blue-50/90',
  scheduled: 'border-indigo-200 bg-indigo-50/90',
  draft: 'border-slate-200 bg-slate-50',
  posted: 'border-slate-200 bg-slate-100/80',
};

const LINKEDIN_LOGO = '/Images/logolinkedin.png';

function LinkedInLogo({ className = 'h-4 w-4 shrink-0 object-contain' }: { className?: string }) {
  return <img src={LINKEDIN_LOGO} alt="LinkedIn" className={className} />;
}

function isDateInCalendarRange(date: Date, rangeStart: Date, rangeEnd: Date): boolean {
  return compareDays(date, rangeStart) >= 0 && compareDays(date, rangeEnd) <= 0;
}

function datePickerCellClass(date: Date, rangeStart: Date, rangeEnd: Date): string {
  if (isSameDay(date, rangeStart) || isSameDay(date, rangeEnd)) {
    return 'bg-blue-600 text-white shadow-[0_4px_12px_-5px_rgba(37,99,235,0.8)]';
  }

  if (isDateInCalendarRange(date, rangeStart, rangeEnd)) {
    return 'bg-blue-100/90 text-blue-900';
  }

  return 'text-slate-700';
}

function getCalendarPickerPanelStyle(anchorRect: DOMRect): CSSProperties {
  const margin = 16;
  const gap = 8;
  const preferredHeight = 500;
  const minHeight = 280;
  const width = Math.min(576, window.innerWidth - margin * 2);
  const left = Math.min(
    Math.max(margin, anchorRect.right - width),
    window.innerWidth - width - margin,
  );

  const viewportBottom = window.innerHeight - margin;
  const spaceAbove = anchorRect.top - margin - gap;
  const spaceBelow = viewportBottom - anchorRect.bottom - gap;
  const canFitAbove = spaceAbove >= minHeight;
  const canFitBelow = spaceBelow >= minHeight;
  const openBelow = !canFitAbove || (canFitBelow && spaceBelow >= spaceAbove);

  let top = openBelow ? anchorRect.bottom + gap : anchorRect.top - Math.min(preferredHeight, spaceAbove) - gap;
  let height = openBelow
    ? Math.min(preferredHeight, spaceBelow)
    : Math.min(preferredHeight, spaceAbove);

  top = Math.max(margin, top);
  height = Math.min(preferredHeight, viewportBottom - top);

  return { left, top, width, height };
}

function CampaignCalendarMonth({
  monthDate,
  rangeStart,
  rangeEnd,
  onSelectDate,
}: {
  monthDate: Date;
  rangeStart: Date;
  rangeEnd: Date;
  onSelectDate?: (date: Date) => void;
}) {
  const cells = buildCalendarCells(monthDate);
  const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-w-[16rem] flex-1">
      <div className="mb-2 flex items-center justify-center">
        <p className="text-xs font-semibold text-slate-700">{monthLabel}</p>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAY_LABELS.map((day) => (
          <span key={day} className="text-[10px] font-medium text-slate-400">
            {day}
          </span>
        ))}
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} className="h-7" />;

          const cellClass = `inline-flex h-7 w-full items-center justify-center rounded-md text-xs font-semibold ${datePickerCellClass(date, rangeStart, rangeEnd)}`;
          if (onSelectDate) {
            return (
              <button
                key={toDateKey(date)}
                type="button"
                onClick={() => onSelectDate(date)}
                className={`${cellClass} transition-colors hover:ring-1 hover:ring-blue-300`}
              >
                {date.getDate()}
              </button>
            );
          }

          return (
            <span key={toDateKey(date)} className={cellClass}>
              {date.getDate()}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function clampAnalyticsRangeEnd(rangeStart: Date, candidateEnd: Date): Date {
  return compareDays(candidateEnd, rangeStart) < 0 ? rangeStart : candidateEnd;
}

function CampaignDateRangePicker({
  start,
  end,
  label,
  onRangeChange,
  triggerClassName,
  open: openProp,
  onOpenChange,
  exclusiveSelectId,
  exclusiveSelectTriggerClass,
  onTriggerPointerDown,
}: {
  start: Date;
  end: Date;
  label: string;
  onRangeChange?: (start: Date, end: Date) => void;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  exclusiveSelectId?: string;
  exclusiveSelectTriggerClass?: string;
  onTriggerPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
}) {
  const { t, localeTag } = useUiLocale();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const skipNextClickRef = useRef(false);
  const isControlled = onOpenChange !== undefined;
  const open = isControlled ? (openProp ?? false) : uncontrolledOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
        return;
      }
      setUncontrolledOpen(value);
    },
    [isControlled, onOpenChange],
  );
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(start));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const interactive = Boolean(onRangeChange);

  const updateAnchorRect = () => {
    const nextRect = triggerRef.current?.getBoundingClientRect();
    if (nextRect) setAnchorRect(nextRect);
  };

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    updateAnchorRect();
    window.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);
    return () => {
      window.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;
    setDraftStart(start);
    setDraftEnd(end);
    setVisibleMonth(startOfMonth(start));
    setSelectingEnd(false);
  }, [end, open, start]);

  const handleSelectDate = (date: Date) => {
    if (!interactive) return;
    if (!selectingEnd) {
      setDraftStart(date);
      setDraftEnd(date);
      setSelectingEnd(true);
      return;
    }
    if (compareDays(date, draftStart) < 0) {
      setDraftStart(date);
      setDraftEnd(draftStart);
    } else {
      setDraftEnd(clampAnalyticsRangeEnd(draftStart, date));
    }
    setSelectingEnd(false);
  };

  const applyDraftRange = () => {
    if (!onRangeChange) return;
    onRangeChange(draftStart, clampAnalyticsRangeEnd(draftStart, draftEnd));
    setOpen(false);
  };

  const panel = open && anchorRect && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={panelRef}
        className={`fixed z-[9999] overflow-y-auto rounded-2xl border border-white/70 bg-white/95 p-3 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.5)] ring-1 ring-blue-100 backdrop-blur-xl ${CAMPAIGN_SCROLLBAR}`}
        style={getCalendarPickerPanelStyle(anchorRect)}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">{t('linkedInSchedule.datePickerTitle')}</p>
            <p className="text-[11px] text-slate-500">
              {interactive
                ? (selectingEnd ? t('campaigns.datePicker.chooseEnd') : t('campaigns.datePicker.chooseStart'))
                : t('campaigns.datePicker.reference')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label={t('campaigns.datePicker.close')}
          >
            x
          </button>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.startDate')}</p>
            <p className="text-xs font-semibold text-slate-800">{formatPickerDate(draftStart, localeTag)}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-[11px] font-medium text-slate-500">{t('linkedInSchedule.endDate')}</p>
            <p className="text-xs font-semibold text-slate-800">{formatPickerDate(draftEnd, localeTag)}</p>
          </div>
        </div>

        {interactive && (
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
              aria-label={t('campaigns.datePicker.prevMonth')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
              aria-label={t('campaigns.datePicker.nextMonth')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex gap-5 border-y border-slate-100 py-3">
          <CampaignCalendarMonth
            monthDate={visibleMonth}
            rangeStart={draftStart}
            rangeEnd={draftEnd}
            onSelectDate={interactive ? handleSelectDate : undefined}
          />
          <CampaignCalendarMonth
            monthDate={addMonths(visibleMonth, 1)}
            rangeStart={draftStart}
            rangeEnd={draftEnd}
            onSelectDate={interactive ? handleSelectDate : undefined}
          />
        </div>

        {interactive && (
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" className="h-8 bg-blue-600 text-xs text-white hover:bg-blue-700" onClick={applyDraftRange}>
              Apply range
            </Button>
          </div>
        )}
      </div>,
      document.body,
    )
    : null;

  const handleTriggerPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!onTriggerPointerDown) return;
    onTriggerPointerDown(event);
    if (event.defaultPrevented) {
      skipNextClickRef.current = true;
    }
  };

  const handleTriggerClick = () => {
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      updateAnchorRect();
      return;
    }
    updateAnchorRect();
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-exclusive-select-id={exclusiveSelectId}
        onPointerDown={onTriggerPointerDown ? handleTriggerPointerDown : undefined}
        onClick={handleTriggerClick}
        className={`${exclusiveSelectTriggerClass ?? ''} ${
          triggerClassName
          ?? 'hidden items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-50 focus:border-blue-300/60 focus:outline-none focus:ring-1 focus:ring-blue-200/80 sm:flex'
        }`}
        aria-expanded={open}
      >
        <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="truncate whitespace-nowrap font-medium tabular-nums">{label}</span>
      </button>
      {panel}
    </>
  );
}

function ScheduleCalendarCard({ post }: { post: CalendarPost }) {
  return (
    <div className={`mb-1.5 rounded-lg border p-2 ${CALENDAR_CARD_STYLE[post.status]}`}>
      <div className="mb-1.5 flex items-start justify-between gap-1">
        <LinkedInLogo className="h-4 w-4 object-contain" />
        {post.check && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-blue-500" strokeWidth={2.5} />}
      </div>
      <div className="mb-1 flex min-w-0 items-center gap-1">
        <Tag className="h-3 w-3 shrink-0 text-blue-600" strokeWidth={2.5} />
        <span className="truncate text-[10px] font-medium leading-tight text-slate-800">{post.label}</span>
      </div>
      <p className="text-[9px] leading-none text-slate-500">{post.time}</p>
    </div>
  );
}

function ScheduleStatusLegend() {
  const statuses = [
    { label: 'Draft', color: 'bg-blue-500' },
    { label: 'Scheduled', color: 'bg-amber-500' },
    { label: 'Approved', color: 'bg-green-500' },
    { label: 'Posted', color: 'bg-slate-400' },
  ];

  return (
    <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-3 text-[11px] xl:flex">
      {statuses.map((status) => (
        <span key={status.label} className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${status.color}`} />
          <span className="text-slate-600">{status.label}</span>
        </span>
      ))}
    </div>
  );
}

function ScheduleDayColumn({
  day,
  posts,
}: {
  day: CalendarDay;
  posts: CalendarPost[];
}) {
  return (
    <div
      className={`border-r border-white/25 transition-colors last:border-r-0 ${
        day.isToday ? 'bg-blue-50/50 hover:bg-blue-100/70' : 'hover:bg-white/30'
      }`}
    >
      <div
        className={`px-2 py-2 text-center text-[11px] font-medium ${
          day.isToday ? 'text-blue-600' : 'text-slate-500'
        }`}
      >
        {day.weekday}{' '}
        {day.isToday ? (
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
            {day.dayNum}
          </span>
        ) : (
          <span className="font-bold text-slate-700">{day.dayNum}</span>
        )}
      </div>
      <div className="min-h-[84px] space-y-0 px-1.5 py-1.5">
        {posts.map((post, index) => (
          <ScheduleCalendarCard key={`${day.key}-${index}`} post={post} />
        ))}
      </div>
    </div>
  );
}

function CampaignContentSchedule({
  scheduleDays,
  calendarPosts,
  scheduleWindowStart,
  scheduleRangeEnd,
  scheduleRangeLabel,
  onToday,
}: {
  scheduleDays: CalendarDay[];
  calendarPosts: Record<string, CalendarPost[]>;
  scheduleWindowStart: Date;
  scheduleRangeEnd: Date;
  scheduleRangeLabel: string;
  onToday: () => void;
}) {
  const { t } = useUiLocale();
  return (
    <div className={`shrink-0 overflow-hidden rounded-2xl ${GLASS_FRAME}`}>
      <div className="relative flex items-center justify-between px-4 py-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{t('campaigns.scheduleTitle')}</h3>
          <p className="text-[11px] text-slate-500">{t('campaigns.scheduleSubtitle')}</p>
        </div>

        <ScheduleStatusLegend />

        <div className="flex items-center gap-2">
          <CampaignDateRangePicker
            start={scheduleWindowStart}
            end={scheduleRangeEnd}
            label={scheduleRangeLabel}
          />
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onToday}>
            {t('common.today')}
          </Button>
        </div>
      </div>

      <div className="border-t border-white/30 px-2 pb-3">
        <div
          className="grid w-full h-full"
          style={{ gridTemplateColumns: `repeat(${SCHEDULE_WINDOW_DAYS}, minmax(0, 1fr))` }}
        >
          {scheduleDays.map((day) => (
            <ScheduleDayColumn
              key={day.key}
              day={day}
              posts={calendarPosts[day.key] ?? []}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

type CampaignScheduleStatus = 'Draft' | 'Scheduled' | 'Approved' | 'Posted';

type CampaignSchedulePost = {
  id: number;
  dayIndex: number;
  hour: number;
  title: string;
  subtitle: string;
  time: string;
  status: CampaignScheduleStatus;
};

const CAMPAIGN_SCHEDULE_POSTS: CampaignSchedulePost[] = [
  { id: 1, dayIndex: 1, hour: 9, title: 'Post 1', subtitle: 'Introducing Our New Feature', time: '09:00 AM', status: 'Approved' },
  { id: 2, dayIndex: 2, hour: 10, title: 'Post 2', subtitle: 'Productivity Tips for Teams', time: '10:30 AM', status: 'Scheduled' },
  { id: 3, dayIndex: 3, hour: 11, title: 'Post 3', subtitle: 'Customer Story: Real Impact', time: '11:00 AM', status: 'Approved' },
  { id: 4, dayIndex: 4, hour: 9, title: 'Post 4', subtitle: 'Behind the Scenes at HAWK', time: '09:30 AM', status: 'Scheduled' },
  { id: 5, dayIndex: 5, hour: 11, title: 'Post 5', subtitle: '5 Ways to Drive More Engagement', time: '11:00 AM', status: 'Approved' },
  { id: 6, dayIndex: 6, hour: 10, title: 'Post 6', subtitle: 'Did You Know? Platform Update', time: '10:00 AM', status: 'Draft' },
  { id: 7, dayIndex: 0, hour: 13, title: 'Post 7', subtitle: 'Weekly Recap: Top Insights', time: '01:00 PM', status: 'Posted' },
  { id: 8, dayIndex: 1, hour: 14, title: 'Post 8', subtitle: 'How We Help Businesses Grow', time: '02:00 PM', status: 'Scheduled' },
  { id: 9, dayIndex: 2, hour: 16, title: 'Post 9', subtitle: 'Industry Trends You Should Know', time: '04:30 PM', status: 'Approved' },
  { id: 10, dayIndex: 4, hour: 13, title: 'Post 10', subtitle: 'New Analytics Dashboard', time: '02:00 PM', status: 'Scheduled' },
  { id: 11, dayIndex: 5, hour: 14, title: 'Post 11', subtitle: 'Tips for Better Decision Making', time: '03:30 PM', status: 'Approved' },
  { id: 12, dayIndex: 6, hour: 15, title: 'Post 12', subtitle: 'Webinar Recap: The Future of AI', time: '05:00 PM', status: 'Scheduled' },
  { id: 13, dayIndex: 3, hour: 18, title: 'Post 13', subtitle: 'Join Our Next Webinar', time: '07:00 PM', status: 'Draft' },
];

const UNSCHEDULED_POSTS = [
  { id: 6, title: 'Post 6', subtitle: 'Q4 Data Driven Platform Update', status: 'Draft' },
  { id: 13, title: 'Post 13', subtitle: 'Join Our Next Webinar', status: 'Draft' },
  { id: 14, title: 'Post 14', subtitle: 'New Feature Spotlight', status: 'Approved' },
  { id: 15, title: 'Post 15', subtitle: 'Client Testimonial', status: 'Approved' },
  { id: 16, title: 'Post 16', subtitle: 'Company Culture Highlight', status: 'Draft' },
] as const;

type CampaignScheduleSlot = {
  postId: number;
  dayKey: string;
  hour: number;
};

const CAMPAIGN_SCHEDULE_RULES = {
  maxPostsPerDay: 2,
  minHoursBetween: 2,
} as const;

const CAMPAIGN_SCHEDULE_REFERENCE_START = startOfDay(new Date(2025, 4, 31));
const SCHEDULE_GRID_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const;
const SCHEDULE_LANE_HEADER_PX = 48;
const SCHEDULE_HOUR_SLOT_PX = 64;

function scheduleHourLabel(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:00 ${period}`;
}

function formatScheduleNowLabel(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function getScheduleCurrentTimeTop(date: Date): number {
  const gridStartHour = SCHEDULE_GRID_HOURS[0];
  const gridEndHour = SCHEDULE_GRID_HOURS[SCHEDULE_GRID_HOURS.length - 1] + 1;
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const startMinutes = gridStartHour * 60;
  const endMinutes = gridEndHour * 60;
  const maxHoursFromStart = SCHEDULE_GRID_HOURS.length;

  let hoursFromStart = (totalMinutes - startMinutes) / 60;
  if (totalMinutes < startMinutes) hoursFromStart = 0;
  if (totalMinutes >= endMinutes) hoursFromStart = maxHoursFromStart;

  return SCHEDULE_LANE_HEADER_PX + hoursFromStart * SCHEDULE_HOUR_SLOT_PX;
}

function useScheduleNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return now;
}

function ScheduleCurrentTimeIndicator() {
  const now = useScheduleNow(30_000);
  const top = getScheduleCurrentTimeTop(now);

  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-50 -translate-y-1/2"
      style={{ top: `${top}px` }}
      aria-hidden
    >
      <div className="flex w-full items-center">
        <div className="flex w-16 shrink-0 justify-center px-1">
          <span className="whitespace-nowrap rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold leading-tight text-white shadow-[0_2px_8px_rgba(239,68,68,0.45)]">
            {formatScheduleNowLabel(now)}
          </span>
        </div>
        <div className="h-[2px] min-w-0 flex-1 bg-red-500" />
      </div>
    </div>
  );
}

function postDateForDayIndex(dayIndex: number): string {
  return formatPostDate(addDays(CAMPAIGN_SCHEDULE_REFERENCE_START, dayIndex));
}

function buildBrandAwarenessQ2Posts(): LinkedInPost[] {
  const posts: LinkedInPost[] = CAMPAIGN_SCHEDULE_POSTS.map((entry) => ({
    id: entry.id,
    title: entry.title,
    date: postDateForDayIndex(entry.dayIndex),
    status: entry.status,
    preview: entry.subtitle,
    content: entry.subtitle,
    hashtags: '#BrandAwareness #Q2',
  }));

  for (const entry of UNSCHEDULED_POSTS) {
    if (posts.some((post) => post.id === entry.id)) continue;
    posts.push({
      id: entry.id,
      title: entry.title,
      date: '—',
      status: entry.status,
      preview: entry.subtitle,
      content: entry.subtitle,
      hashtags: '#BrandAwareness #Q2',
    });
  }

  return posts.sort((a, b) => a.id - b.id);
}

function buildInitialScheduleSlots(): Record<number, CampaignScheduleSlot> {
  const slots: Record<number, CampaignScheduleSlot> = {};
  for (const entry of CAMPAIGN_SCHEDULE_POSTS) {
    slots[entry.id] = {
      postId: entry.id,
      dayKey: toDateKey(addDays(CAMPAIGN_SCHEDULE_REFERENCE_START, entry.dayIndex)),
      hour: entry.hour,
    };
  }
  return slots;
}

function getBrandAwarenessScheduleWindow(): { start: Date; end: Date } {
  return {
    start: CAMPAIGN_SCHEDULE_REFERENCE_START,
    end: addDays(CAMPAIGN_SCHEDULE_REFERENCE_START, SCHEDULE_WINDOW_DAYS - 1),
  };
}

function addConflictMessage(
  target: Record<number, string[]>,
  postId: number,
  message: string,
) {
  const existing = target[postId] ?? [];
  if (existing.includes(message)) return;
  target[postId] = [...existing, message];
}

function detectScheduleConflicts(
  slots: Record<number, CampaignScheduleSlot>,
  posts: LinkedInPost[],
  t: TranslateFn,
): Record<number, string[]> {
  const conflicts: Record<number, string[]> = {};
  const postsById = new Map(posts.map((post) => [post.id, post]));
  const byDay: Record<string, Array<{ postId: number; hour: number }>> = {};

  for (const slot of Object.values(slots)) {
    const post = postsById.get(slot.postId);
    if (post?.status === 'Posted') {
      addConflictMessage(conflicts, slot.postId, t('campaigns.errPostedCannotReschedule'));
    }
    (byDay[slot.dayKey] ??= []).push({ postId: slot.postId, hour: slot.hour });
  }

  for (const daySlots of Object.values(byDay)) {
    if (daySlots.length > CAMPAIGN_SCHEDULE_RULES.maxPostsPerDay) {
      for (const entry of daySlots) {
        addConflictMessage(
          conflicts,
          entry.postId,
          `Max ${CAMPAIGN_SCHEDULE_RULES.maxPostsPerDay} posts per day (campaign rule)`,
        );
      }
    }

    const sorted = [...daySlots].sort((a, b) => a.hour - b.hour);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].hour - sorted[i - 1].hour < CAMPAIGN_SCHEDULE_RULES.minHoursBetween) {
        addConflictMessage(conflicts, sorted[i].postId, `Min ${CAMPAIGN_SCHEDULE_RULES.minHoursBetween}h between posts`);
        addConflictMessage(conflicts, sorted[i - 1].postId, `Min ${CAMPAIGN_SCHEDULE_RULES.minHoursBetween}h between posts`);
      }
    }
  }

  return conflicts;
}

function matchesScheduleStatusFilter(
  status: CampaignScheduleStatus,
  filter: 'all' | CampaignScheduleStatus,
): boolean {
  return filter === 'all' || status === filter;
}

function getScheduleTemplate(postId: number): CampaignSchedulePost | undefined {
  return CAMPAIGN_SCHEDULE_POSTS.find((post) => post.id === postId);
}

function parseScheduleHourLabel(time: string): number {
  const match = time.match(/(\d{1,2})(?::\d{2})?\s*(AM|PM)?/i);
  if (!match) return SCHEDULE_GRID_HOURS[0];

  const value = Number(match[1]);
  const period = match[2]?.toUpperCase();
  if (period === 'PM' && value < 12) return value + 12;
  if (period === 'AM' && value === 12) return 0;
  return value;
}

function buildSharedScheduleSlotsByCampaign(): Record<number, Record<number, CampaignScheduleSlot>> {
  return readSharedScheduledPosts().reduce<Record<number, Record<number, CampaignScheduleSlot>>>(
    (result, sharedPost) => {
      const day = parseDateKey(sharedPost.dateKey);
      if (!day) return result;

      const campaignSlots = result[sharedPost.campaignId] ?? {};
      result[sharedPost.campaignId] = {
        ...campaignSlots,
        [sharedPost.id]: {
          postId: sharedPost.id,
          dayKey: sharedPost.dateKey,
          hour: parseScheduleHourLabel(sharedPost.time),
        },
      };
      return result;
    },
    {},
  );
}

function getInitialScheduleSlotsByCampaign(): Record<number, Record<number, CampaignScheduleSlot>> {
  const sharedSlots = buildSharedScheduleSlotsByCampaign();
  return {
    ...sharedSlots,
    1: {
      ...buildInitialScheduleSlots(),
      ...(sharedSlots[1] ?? {}),
    },
  };
}

function writeCampaignScheduleSharedPost({
  campaign,
  post,
  dayKey,
  hour,
  languageLabel,
}: {
  campaign: LinkedInCampaign;
  post: LinkedInPost;
  dayKey: string;
  hour: number;
  languageLabel: string;
}) {
  upsertSharedScheduledPost({
    id: post.id,
    campaignId: campaign.id,
    campaignName: campaign.name,
    campaignType: campaign.type,
    title: post.title || `Post ${post.id}`,
    description: post.content || post.preview,
    dateKey: dayKey,
    time: scheduleHourLabel(hour),
    status: 'Scheduled',
    language: languageLabel,
  });
}

const CAMPAIGN_SCHEDULE_STATUS_STYLE: Record<CampaignScheduleStatus, string> = {
  Draft: 'border-blue-200 bg-blue-50 text-blue-700',
  Scheduled: 'border-amber-200 bg-amber-50 text-amber-700',
  Approved: 'border-green-200 bg-green-50 text-green-700',
  Posted: 'border-slate-200 bg-slate-50 text-slate-600',
};

const CAMPAIGN_SCHEDULE_DOT_STYLE: Record<CampaignScheduleStatus, string> = {
  Draft: 'bg-blue-500',
  Scheduled: 'bg-amber-500',
  Approved: 'bg-green-500',
  Posted: 'bg-slate-400',
};

function StrategyCard({
  icon: Icon,
  title,
  tone,
  children,
}: {
  icon: LucideIcon;
  title: string;
  tone: string;
  children: ReactNode;
}) {
  return (
    <div className={`flex h-full flex-col rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <span className="text-xs text-red-400">*</span>
      </div>
      {children}
    </div>
  );
}

function StrategyTextarea({
  value,
  onChange,
  maxLength,
  error,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  error?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`min-h-[76px] w-full resize-none rounded-lg border bg-slate-50/60 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 ${
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
            : 'border-slate-200 focus:border-blue-300 focus:ring-blue-200'
        }`}
      />
      <p className={`mt-1 text-right text-[11px] ${error ? 'text-red-500' : 'text-slate-400'}`}>
        {error ?? `${value.length} / ${maxLength}`}
      </p>
    </div>
  );
}

function StrategySelectRow({
  icon: Icon,
  label,
  value,
  onValueChange,
  children,
  open,
  onOpenChange,
  onSelectContentOutside,
  onTriggerPointerDown,
  exclusiveSelectId,
  exclusiveSelectTriggerClass,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectContentOutside?: (event: Event) => void;
  onTriggerPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  exclusiveSelectId?: string;
  exclusiveSelectTriggerClass?: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
      <SelectTrigger
        data-exclusive-select-id={exclusiveSelectId}
        onPointerDown={onTriggerPointerDown}
        className={`${exclusiveSelectTriggerClass ?? ''} flex h-auto min-h-[52px] w-full flex-1 items-center gap-3 rounded-xl border-0 px-3 py-2.5 shadow-none focus:ring-1 focus:ring-blue-200 ${LIQUID_GLASS_SOFT}`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-xs font-semibold text-slate-700">{label}</p>
          <SelectValue className="truncate text-xs text-slate-500" />
        </div>
      </SelectTrigger>
      <SelectContent onPointerDownOutside={onSelectContentOutside}>{children}</SelectContent>
    </Select>
  );
}

function StrategyAddButton({
  onClick,
  disabled,
  label = 'Add item',
}: {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-blue-600 transition-colors hover:bg-blue-100 active:bg-blue-200 disabled:opacity-40"
    >
      <Plus className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );
}

function RemoveListItemButton({
  label,
  onClick,
  size = 'md',
}: {
  label: string;
  onClick: () => void;
  size?: 'sm' | 'md';
}) {
  const buttonClass = size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Remove ${label}`}
      className={`inline-flex ${buttonClass} shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-500 hover:text-white active:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400`}
    >
      <X className={iconClass} strokeWidth={2.5} />
    </button>
  );
}

function StrategyChipInput({
  items,
  onChange,
  placeholder,
  maxItems,
  maxItemLength,
  error,
  normalize,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  maxItems: number;
  maxItemLength: number;
  error?: string;
  normalize?: (value: string) => string;
}) {
  const [draft, setDraft] = useState('');

  const addChip = () => {
    const raw = normalize ? normalize(draft) : draft.trim();
    if (!raw || items.length >= maxItems || raw.length > maxItemLength) return;
    if (items.includes(raw)) {
      setDraft('');
      return;
    }
    onChange([...items, raw]);
    setDraft('');
  };

  const removeChip = (item: string) => {
    onChange(items.filter((entry) => entry !== item));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-slate-700"
          >
            {item}
            <RemoveListItemButton label={item} onClick={() => removeChip(item)} size="sm" />
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addChip();
            }
          }}
          placeholder={placeholder}
          maxLength={maxItemLength}
          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
        />
        <StrategyAddButton onClick={addChip} disabled={items.length >= maxItems} label="Add chip" />
      </div>
      {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

function StrategySortableList({
  items,
  onChange,
  addLabel,
  maxItems,
  maxItemLength,
  error,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  addLabel: string;
  maxItems: number;
  maxItemLength: number;
  error?: string;
}) {
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const trimmed = draft.trim();
    if (!trimmed || items.length >= maxItems || trimmed.length > maxItemLength) return;
    if (items.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...items, trimmed]);
    setDraft('');
  };

  const removeItem = (item: string) => {
    onChange(items.filter((entry) => entry !== item));
  };

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <span className="text-slate-300">::</span>
          <span className="flex-1">{item}</span>
          <RemoveListItemButton label={item} onClick={() => removeItem(item)} />
        </div>
      ))}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addItem();
            }
          }}
          placeholder={addLabel}
          maxLength={maxItemLength}
          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1.5 text-xs focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
        />
        <StrategyAddButton onClick={addItem} disabled={items.length >= maxItems} label="Add list item" />
      </div>
      {error && <p className="text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

type StrategyFormProps = {
  data: CampaignStrategyData;
  errors: Partial<Record<StrategyFieldKey, string>>;
  onChange: (patch: Partial<CampaignStrategyData>) => void;
};

function StrategyOverviewHeader({
  savedAt,
  isDirty,
  hasErrors,
  saveNotice,
}: {
  savedAt: string | null;
  isDirty: boolean;
  hasErrors: boolean;
  saveNotice: string | null;
}) {
  const { t, localeTag } = useUiLocale();
  return (
    <div className="flex items-center justify-between border-b border-white/30 px-6 py-3">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{t('campaigns.strategyOverview')}</h3>
        <p className="text-xs text-slate-500">
          {t('campaigns.strategyOverviewDesc')}
        </p>
        {saveNotice && <p className="mt-1 text-[11px] font-medium text-green-600">{saveNotice}</p>}
      </div>
      <div className="flex flex-col items-end gap-1 text-[11px]">
        <span className="text-slate-400">{formatStrategySavedLabel(savedAt, t, localeTag)}</span>
        {hasErrors ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 font-medium text-red-700">
            <AlertTriangle className="h-3 w-3" /> {t('campaigns.fixRequired')}
          </span>
        ) : isDirty ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700">
            {t('campaigns.unsavedChanges')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 font-medium text-green-700">
            <CheckCircle2 className="h-3 w-3" /> {t('common.saved')}
          </span>
        )}
      </div>
    </div>
  );
}

function StrategyPrimaryGrid({ data, errors, onChange }: StrategyFormProps) {
  const { t } = useUiLocale();
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'language' | 'timeZone' | 'category'>('strategy-primary');

  return (
    <>
      <div className="col-span-4 h-full">
        <StrategyCard icon={Target} title={t('campaigns.objective')} tone="bg-purple-50 text-purple-600">
          <StrategyTextarea
            value={data.objective}
            onChange={(objective) => onChange({ objective })}
            maxLength={STRATEGY_LIMITS.objective}
            error={errors.objective}
          />
        </StrategyCard>
      </div>
      <div className="col-span-4 h-full">
        <StrategyCard icon={Users} title={t('campaigns.targetAudience')} tone="bg-purple-50 text-purple-600">
          <StrategyTextarea
            value={data.targetAudience}
            onChange={(targetAudience) => onChange({ targetAudience })}
            maxLength={STRATEGY_LIMITS.targetAudience}
            error={errors.targetAudience}
          />
        </StrategyCard>
      </div>
      <div
        className="col-span-4 flex h-full flex-col gap-3"
        data-exclusive-select-group={exclusiveSelectGroupId}
      >
        <StrategySelectRow
          icon={Globe2}
          label={t('campaigns.language')}
          value={data.language}
          open={openSelect === 'language'}
          exclusiveSelectId="language"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('language')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('language')}
          onValueChange={(language) => {
            onChange({ language: language as LanguageCode });
            setOpenSelect(null);
          }}
        >
          {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
            <SelectItem key={code} value={code}>
              <span className="flex items-center gap-2">
                <LanguageFlagByCode code={code} />
                {LANGUAGE_OPTIONS[code].label}
              </span>
            </SelectItem>
          ))}
        </StrategySelectRow>
        <StrategySelectRow
          icon={Clock3}
          label={t('campaigns.settings.timeZone')}
          value={data.timeZone}
          open={openSelect === 'timeZone'}
          exclusiveSelectId="timeZone"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('timeZone')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('timeZone')}
          onValueChange={(timeZone) => {
            onChange({ timeZone: timeZone as StrategyTimeZoneId });
            setOpenSelect(null);
          }}
        >
          {STRATEGY_TIME_ZONE_ORDER.map((id) => (
            <SelectItem key={id} value={id}>
              {getStrategyTimeZoneLabel(id, t)}
            </SelectItem>
          ))}
        </StrategySelectRow>
        <StrategySelectRow
          icon={Tag}
          label={t('campaigns.category')}
          value={data.category}
          open={openSelect === 'category'}
          exclusiveSelectId="category"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('category')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('category')}
          onValueChange={(category) => {
            onChange({ category: category as StrategyCategoryId });
            setOpenSelect(null);
          }}
        >
          {(['saas-tech', 'b2b-marketing', 'fintech', 'healthcare'] as StrategyCategoryId[]).map((id) => (
            <SelectItem key={id} value={id}>
              {getStrategyCategoryLabel(id, t)}
            </SelectItem>
          ))}
        </StrategySelectRow>
      </div>
    </>
  );
}

function StrategyVoiceGrid({ data, errors, onChange }: StrategyFormProps) {
  const { t } = useUiLocale();
  return (
    <>
      <div className="col-span-6 h-full">
        <StrategyCard icon={MessageSquare} title={t('campaigns.toneOfVoice')} tone="bg-blue-50 text-blue-600">
          <StrategyChipInput
            items={data.toneOfVoice}
            onChange={(toneOfVoice) => onChange({ toneOfVoice })}
            placeholder={t('campaigns.tonePlaceholder')}
            maxItems={STRATEGY_LIMITS.maxTones}
            maxItemLength={STRATEGY_LIMITS.chipItem}
            error={errors.toneOfVoice}
          />
          <p className="mt-4 text-xs text-slate-500">The overall tone and style of communication for this campaign.</p>
          <div className="mt-2">
            <StrategyTextarea
              value={data.toneDescription}
              onChange={(toneDescription) => onChange({ toneDescription })}
              maxLength={STRATEGY_LIMITS.toneDescription}
              error={errors.toneDescription}
              placeholder={t('campaigns.toneNotesPlaceholder')}
            />
          </div>
        </StrategyCard>
      </div>
      <div className="col-span-6 h-full">
        <StrategyCard icon={ShieldCheck} title={t('campaigns.brandGuidelines')} tone="bg-blue-50 text-blue-600">
          <StrategyTextarea
            value={data.brandGuidelines}
            onChange={(brandGuidelines) => onChange({ brandGuidelines })}
            maxLength={STRATEGY_LIMITS.brandGuidelines}
            error={errors.brandGuidelines}
          />
          <p className="mt-4 text-xs font-semibold text-slate-600">Competitor References</p>
          <div className="mt-2">
            <StrategyChipInput
              items={data.competitorReferences}
              onChange={(competitorReferences) => onChange({ competitorReferences })}
              placeholder={t('campaigns.competitorPlaceholder')}
              maxItems={STRATEGY_LIMITS.maxCompetitors}
              maxItemLength={STRATEGY_LIMITS.chipItem}
              error={errors.competitorReferences}
            />
          </div>
        </StrategyCard>
      </div>
    </>
  );
}

function StrategyListsGrid({ data, errors, onChange }: StrategyFormProps) {
  const { t } = useUiLocale();
  return (
    <>
      <div className="col-span-6 h-full">
        <StrategyCard icon={Lightbulb} title={t('campaigns.contentPillars')} tone="bg-green-50 text-green-600">
          <StrategySortableList
            items={data.contentPillars}
            onChange={(contentPillars) => onChange({ contentPillars })}
            addLabel={t('campaigns.addPillar')}
            maxItems={STRATEGY_LIMITS.maxPillars}
            maxItemLength={STRATEGY_LIMITS.listItem}
            error={errors.contentPillars}
          />
        </StrategyCard>
      </div>
      <div className="col-span-6 h-full">
        <StrategyCard icon={Sparkles} title={t('campaigns.keyMessages')} tone="bg-orange-50 text-orange-600">
          <StrategySortableList
            items={data.keyMessages}
            onChange={(keyMessages) => onChange({ keyMessages })}
            addLabel={t('campaigns.addMessage')}
            maxItems={STRATEGY_LIMITS.maxMessages}
            maxItemLength={STRATEGY_LIMITS.listItem}
            error={errors.keyMessages}
          />
        </StrategyCard>
      </div>
    </>
  );
}

function StrategyPlanningGrid({ data, errors, onChange }: StrategyFormProps) {
  const { t } = useUiLocale();
  return (
    <>
      <div className="col-span-6 h-full">
        <StrategyCard icon={Hash} title={t('campaigns.hashtagStrategy')} tone="bg-purple-50 text-purple-600">
          <p className="mb-3 text-xs text-slate-500">Define hashtags to use consistently in your posts.</p>
          <StrategyChipInput
            items={data.hashtagStrategy}
            onChange={(hashtagStrategy) => onChange({ hashtagStrategy })}
            placeholder={t('campaigns.hashtagPlaceholder')}
            maxItems={STRATEGY_LIMITS.maxHashtags}
            maxItemLength={STRATEGY_LIMITS.chipItem}
            error={errors.hashtagStrategy}
            normalize={(value) => (value.startsWith('#') ? value : `#${value}`)}
          />
          <div className="mt-3">
            <StrategyTextarea
              value={data.hashtagNotes}
              onChange={(hashtagNotes) => onChange({ hashtagNotes })}
              maxLength={STRATEGY_LIMITS.hashtagNotes}
              error={errors.hashtagNotes}
              placeholder={t('campaigns.hashtagNotesPlaceholder')}
            />
          </div>
        </StrategyCard>
      </div>
      <div className="col-span-3 h-full">
        <StrategyCard icon={Calendar} title={t('campaigns.postingFrequency')} tone="bg-teal-50 text-teal-600">
          <input
            type="text"
            value={data.postingFrequency}
            onChange={(event) => onChange({ postingFrequency: event.target.value })}
            maxLength={STRATEGY_LIMITS.postingFrequency}
            className={`w-full rounded-lg border bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 ${
              errors.postingFrequency
                ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
                : 'border-slate-200 focus:border-blue-300 focus:ring-blue-200'
            }`}
          />
          {errors.postingFrequency && (
            <p className="mt-1 text-[11px] text-red-500">{errors.postingFrequency}</p>
          )}
          <p className="mt-3 text-xs text-slate-500">Recommended frequency for this campaign.</p>
        </StrategyCard>
      </div>
      <div className="col-span-3 h-full">
        <StrategyCard icon={Clock3} title={t('campaigns.preferredPostingTime')} tone="bg-blue-50 text-blue-600">
          <StrategyChipInput
            items={data.preferredPostingTimes}
            onChange={(preferredPostingTimes) => onChange({ preferredPostingTimes })}
            placeholder="e.g. 09:00 AM"
            maxItems={STRATEGY_LIMITS.maxTimes}
            maxItemLength={STRATEGY_LIMITS.chipItem}
            error={errors.preferredPostingTimes}
          />
          <p className="mt-3 text-xs text-slate-500">
            Times are in {getStrategyTimeZoneLabel(data.timeZone, t)}
          </p>
        </StrategyCard>
      </div>
    </>
  );
}

function StrategyActionFooter({
  isDirty,
  isSaving,
  isGenerating,
  onReset,
  onSave,
  onGenerateAi,
}: {
  isDirty: boolean;
  isSaving: boolean;
  isGenerating: boolean;
  onReset: () => void;
  onSave: () => void;
  onGenerateAi: () => void;
}) {
  const { t } = useUiLocale();
  return (
    <div className="flex items-center justify-between border-t border-white/30 px-5 py-3">
      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={onReset} disabled={!isDirty || isSaving}>
        <RotateCcw className="h-3.5 w-3.5" /> {t('campaigns.resetChanges')}
      </Button>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-slate-400">
          {isDirty ? t('campaigns.strategyDirtyHint') : t('campaigns.strategySynced')}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={onGenerateAi}
          disabled={isGenerating || isSaving}
        >
          <Sparkles className="h-3.5 w-3.5 text-blue-600" />
          {isGenerating ? t('campaigns.generating') : t('campaigns.generateStrategyAi')}
        </Button>
        <Button
          size="sm"
          className="gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700"
          onClick={onSave}
          disabled={isSaving || isGenerating}
        >
          <Save className="h-3.5 w-3.5" /> {isSaving ? t('campaigns.saving') : t('campaigns.saveStrategy')}
        </Button>
      </div>
    </div>
  );
}

function CampaignStrategyPanel({
  formProps,
  savedAt,
  isDirty,
  hasErrors,
  saveNotice,
  isSaving,
  isGenerating,
  onReset,
  onSave,
  onGenerateAi,
}: {
  formProps: StrategyFormProps;
  savedAt: string | null;
  isDirty: boolean;
  hasErrors: boolean;
  saveNotice: string | null;
  isSaving: boolean;
  isGenerating: boolean;
  onReset: () => void;
  onSave: () => void;
  onGenerateAi: () => void;
}) {
  return (
    <div className={`flex min-h-0 flex-1 flex-col ${CAMPAIGN_SURFACE_BG}`}>
      <StrategyOverviewHeader
        savedAt={savedAt}
        isDirty={isDirty}
        hasErrors={hasErrors}
        saveNotice={saveNotice}
      />

      <div className={`min-h-0 flex-1 overflow-y-auto p-5 ${CAMPAIGN_SCROLLBAR}`}>
        <div className="grid grid-cols-12 items-stretch gap-4">
          <StrategyPrimaryGrid {...formProps} />
          <StrategyVoiceGrid {...formProps} />
          <StrategyListsGrid {...formProps} />
          <StrategyPlanningGrid {...formProps} />
        </div>
      </div>

      <StrategyActionFooter
        isDirty={isDirty}
        isSaving={isSaving}
        isGenerating={isGenerating}
        onReset={onReset}
        onSave={onSave}
        onGenerateAi={onGenerateAi}
      />
    </div>
  );
}

function StrategyAiPreviewCard({ preview }: { preview: StrategyAiPreview }) {
  return (
    <div className={`mt-4 rounded-xl border border-blue-200/80 p-3 ${LIQUID_GLASS_SOFT}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-600">AI preview only</p>
      <p className="mt-1 text-[11px] text-slate-500">
        Generated for {preview.campaignName}. Review and copy into the form — not applied automatically.
      </p>
      <div className="mt-3 space-y-2 text-xs text-slate-700">
        <p><strong>Brand positioning:</strong> {preview.brandPositioning}</p>
        <p><strong>Content pillars:</strong> {preview.contentPillars.join(', ')}</p>
        <p><strong>Tone of voice:</strong> {preview.toneOfVoice.join(', ')}</p>
        <p><strong>Key messages:</strong> {preview.keyMessages.join(' | ')}</p>
        <p><strong>Hashtag plan:</strong> {preview.hashtagPlan.join(' ')}</p>
      </div>
    </div>
  );
}

function StrategyAssistantPanel({
  aiPreview,
  isGenerating,
  onGenerateAi,
}: {
  aiPreview: StrategyAiPreview | null;
  isGenerating: boolean;
  onGenerateAi: () => void;
}) {
  const { t } = useUiLocale();
  const suggestions = [
    { title: t('campaigns.suggestPillars'), text: t('campaigns.suggestPillarsDesc'), tone: 'bg-green-50 text-green-600' },
    { title: t('campaigns.suggestMessages'), text: t('campaigns.suggestMessagesDesc'), tone: 'bg-orange-50 text-orange-600' },
    { title: t('campaigns.suggestHashtags'), text: t('campaigns.suggestHashtagsDesc'), tone: 'bg-purple-50 text-purple-600' },
    { title: t('campaigns.suggestTimes'), text: t('campaigns.suggestTimesDesc'), tone: 'bg-cyan-50 text-cyan-600' },
  ];

  return (
    <>
      <div className={`flex-1 overflow-y-auto px-4 py-4 ${CAMPAIGN_SCROLLBAR}`}>
        <p className="rounded-xl bg-slate-50 px-3.5 py-3 text-sm leading-relaxed text-slate-700">
          Hi! I&apos;m your AI assistant. I can help you generate a complete strategy for your LinkedIn campaign based on your objective and audience.
        </p>
        <Button
          className="mt-4 w-full gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700"
          onClick={onGenerateAi}
          disabled={isGenerating}
        >
          <Sparkles className="h-3.5 w-3.5" /> {isGenerating ? t('campaigns.generating') : t('campaigns.generateStrategy')}
        </Button>
        {aiPreview && <StrategyAiPreviewCard preview={aiPreview} />}
        <div className="mt-5 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Quick Suggestions</h4>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.title}
              type="button"
              className={`flex w-full gap-3 rounded-xl p-3 text-left transition-colors hover:bg-white/50 ${LIQUID_GLASS_SOFT}`}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${suggestion.tone}`}>
                <Sparkles className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-xs font-semibold text-slate-900">{suggestion.title}</span>
                <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">{suggestion.text}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-white/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder={t('campaigns.askAnything')}
            className="flex-1 rounded-lg border border-white/45 bg-white/45 px-3 py-2 text-sm backdrop-blur-md focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
          />
          <button type="button" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700">
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400">
          AI suggestions are preview-only. Save strategy in the form to use context for per-post generation.
        </p>
      </div>
    </>
  );
}

const SCHEDULE_VIEW_MODES = ['Week', 'Month', 'List'] as const;
const SCHEDULE_TOOLBAR_HEIGHT = 'h-9';
const SCHEDULE_TOOLBAR_TEXT = 'text-xs font-medium text-slate-700';
const SCHEDULE_TOOLBAR_CLUSTER =
  `inline-flex ${SCHEDULE_TOOLBAR_HEIGHT} items-center rounded-xl border border-slate-200/70 bg-white/70 shadow-sm backdrop-blur-md`;
const SCHEDULE_TOOLBAR_SELECT =
  `${SCHEDULE_TOOLBAR_HEIGHT} border-slate-200/80 bg-white py-0 ${SCHEDULE_TOOLBAR_TEXT} [&_[data-slot=select-value]]:text-xs [&_[data-slot=select-value]]:text-slate-700 [&>span]:truncate`;
const SCHEDULE_TOOLBAR_BUTTON =
  `inline-flex ${SCHEDULE_TOOLBAR_HEIGHT} shrink-0 items-center rounded-xl border border-slate-200/80 bg-white px-4 ${SCHEDULE_TOOLBAR_TEXT} transition-colors hover:bg-slate-50`;

function ScheduleToolbarIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`flex ${SCHEDULE_TOOLBAR_HEIGHT} w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800`}
    >
      {children}
    </button>
  );
}

function ScheduleToolbarToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const inputId = 'schedule-show-unscheduled';

  return (
    <label htmlFor={inputId} className={`flex ${SCHEDULE_TOOLBAR_HEIGHT} shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap`}>
      <span className={SCHEDULE_TOOLBAR_TEXT}>{label}</span>
      <span className="hawk-toggle-switch hawk-toggle-switch-sm shrink-0">
        <span className="switch">
          <input
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span className="slider" aria-hidden="true" />
        </span>
      </span>
    </label>
  );
}

function ScheduleToolbar({
  rangeLabel,
  start,
  end,
  timeZone,
  statusFilter,
  showUnscheduled,
  onToday,
  onPrevWeek,
  onNextWeek,
  onTimeZoneChange,
  onStatusFilterChange,
  onShowUnscheduledChange,
}: {
  rangeLabel: string;
  start: Date;
  end: Date;
  timeZone: StrategyTimeZoneId;
  statusFilter: 'all' | CampaignScheduleStatus;
  showUnscheduled: boolean;
  onToday: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTimeZoneChange: (timeZone: StrategyTimeZoneId) => void;
  onStatusFilterChange: (value: 'all' | CampaignScheduleStatus) => void;
  onShowUnscheduledChange: (value: boolean) => void;
}) {
  const { t } = useUiLocale();
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'dateRange' | 'timeZone' | 'status'>('schedule-toolbar');

  return (
    <div className={`shrink-0 border-b border-white/30 px-4 py-3 ${LIQUID_GLASS_SOFT}`}>
      <div
        className="flex w-full flex-nowrap items-center justify-between gap-4"
        data-exclusive-select-group={exclusiveSelectGroupId}
      >
        <div className={`flex ${SCHEDULE_TOOLBAR_HEIGHT} min-w-0 flex-1 items-center gap-3`}>
          <div className={`shrink-0 ${SCHEDULE_TOOLBAR_CLUSTER}`}>
            {SCHEDULE_VIEW_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                className={`flex h-full items-center rounded-lg px-3.5 ${SCHEDULE_TOOLBAR_TEXT} transition-colors ${
                  mode === 'Week'
                    ? 'bg-blue-600 font-medium text-white shadow-sm'
                    : 'hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {t(SCHEDULE_VIEW_MODE_I18N[mode])}
              </button>
            ))}
          </div>

          <div className={`min-w-0 flex-1 ${SCHEDULE_TOOLBAR_CLUSTER}`}>
            <ScheduleToolbarIconButton label={t('campaigns.prevWeek')} onClick={onPrevWeek}>
              <ChevronLeft className="h-4 w-4" />
            </ScheduleToolbarIconButton>
            <CampaignDateRangePicker
              start={start}
              end={end}
              label={rangeLabel}
              open={openSelect === 'dateRange'}
              exclusiveSelectId="dateRange"
              exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
              onOpenChange={onOpenChange('dateRange')}
              onTriggerPointerDown={onTriggerPointerDown('dateRange')}
              triggerClassName={`flex ${SCHEDULE_TOOLBAR_HEIGHT} min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 ${SCHEDULE_TOOLBAR_TEXT} transition-colors hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-200`}
            />
            <ScheduleToolbarIconButton label={t('campaigns.nextWeek')} onClick={onNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </ScheduleToolbarIconButton>
          </div>

          <button type="button" className={SCHEDULE_TOOLBAR_BUTTON} onClick={onToday}>
            {t('common.today')}
          </button>
        </div>

        <div className={`flex ${SCHEDULE_TOOLBAR_HEIGHT} shrink-0 items-center gap-4`}>
          <Select
            value={timeZone}
            open={openSelect === 'timeZone'}
            onOpenChange={onOpenChange('timeZone')}
            onValueChange={(value) => {
              onTimeZoneChange(value as StrategyTimeZoneId);
              setOpenSelect(null);
            }}
          >
            <SelectTrigger
              data-exclusive-select-id="timeZone"
              onPointerDown={onTriggerPointerDown('timeZone')}
              className={`${exclusiveSelectTriggerClass} w-[min(280px,24vw)] min-w-[200px] ${SCHEDULE_TOOLBAR_SELECT}`}
            >
              <Globe2 className="mr-1 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent onPointerDownOutside={onSelectContentOutside}>
              {STRATEGY_TIME_ZONE_ORDER.map((id) => (
                <SelectItem key={id} value={id}>
                  {getStrategyTimeZoneLabel(id, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={statusFilter}
            open={openSelect === 'status'}
            onOpenChange={onOpenChange('status')}
            onValueChange={(v) => {
              onStatusFilterChange(v as 'all' | CampaignScheduleStatus);
              setOpenSelect(null);
            }}
          >
            <SelectTrigger
              data-exclusive-select-id="status"
              onPointerDown={onTriggerPointerDown('status')}
              className={`${exclusiveSelectTriggerClass} w-[140px] ${SCHEDULE_TOOLBAR_SELECT}`}
            >
              <List className="mr-1 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent onPointerDownOutside={onSelectContentOutside}>
              <SelectItem value="all">{t('campaigns.allStatus')}</SelectItem>
              <SelectItem value="Draft">{translatePostStatus('Draft', t)}</SelectItem>
              <SelectItem value="Scheduled">{translatePostStatus('Scheduled', t)}</SelectItem>
              <SelectItem value="Approved">{translatePostStatus('Approved', t)}</SelectItem>
              <SelectItem value="Posted">{translatePostStatus('Posted', t)}</SelectItem>
            </SelectContent>
          </Select>

          <ScheduleToolbarToggle
            label={t('campaigns.showUnscheduled')}
            checked={showUnscheduled}
            onChange={onShowUnscheduledChange}
          />
        </div>
      </div>
    </div>
  );
}

function ScheduleLegendRow() {
  const { t } = useUiLocale();
  return (
    <div className={`flex h-9 items-center justify-center gap-5 border-b border-white/35 text-[11px] ${CAMPAIGN_SURFACE_BG}`}>
      {(['Draft', 'Scheduled', 'Approved', 'Posted'] as CampaignScheduleStatus[]).map((status) => (
        <span key={status} className="flex items-center gap-1.5 text-slate-500">
          <span className={`h-2 w-2 rounded-full ${CAMPAIGN_SCHEDULE_DOT_STYLE[status]}`} />
          {translatePostStatus(status, t)}
        </span>
      ))}
    </div>
  );
}

type ScheduleGridPost = {
  id: number;
  title: string;
  subtitle: string;
  time: string;
  status: CampaignScheduleStatus;
  hour: number;
};

function SchedulePostCard({
  post,
  isSelected,
  hasConflict,
  onSelect,
  onDragStart,
  onDragEnd,
}: {
  post: ScheduleGridPost;
  isSelected: boolean;
  hasConflict: boolean;
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const { t } = useUiLocale();
  return (
    <button
      type="button"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/post-id', String(post.id));
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      className={`absolute left-2 right-2 cursor-grab rounded-lg p-2 text-left backdrop-blur-xl active:cursor-grabbing ${CAMPAIGN_SCHEDULE_STATUS_STYLE[post.status]} ${
        isSelected
          ? `z-20 ${SELECTED_POST_CARD}`
          : 'z-10 shadow-sm'
      } ${hasConflict && !isSelected ? 'ring-2 ring-red-400/90' : ''} ${hasConflict && isSelected ? 'ring-2 ring-red-400' : ''}`}
      style={{ top: `${SCHEDULE_LANE_HEADER_PX + 8 + (post.hour - SCHEDULE_GRID_HOURS[0]) * SCHEDULE_HOUR_SLOT_PX}px` }}
    >
      <div className="mb-1 flex items-start justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-slate-300">::</span>
          <LinkedInLogo className="h-3.5 w-3.5 object-contain" />
          <p className="truncate text-xs font-semibold text-slate-900">{post.title}</p>
        </div>
        <Badge className={`shrink-0 border px-1.5 py-0 text-[9px] ${CAMPAIGN_SCHEDULE_STATUS_STYLE[post.status]}`}>
          {translatePostStatus(post.status, t)}
        </Badge>
      </div>
      <p className="line-clamp-2 text-[10px] leading-tight text-slate-600">{post.subtitle}</p>
      <p className="mt-1 text-right text-[9px] text-slate-500">{post.time}</p>
      {hasConflict && (
        <p className="mt-1 flex items-center gap-1 text-[9px] font-medium text-red-600">
          <AlertTriangle className="h-3 w-3" /> {t('campaigns.conflict')}
        </p>
      )}
    </button>
  );
}

function ScheduleDayLane({
  day,
  posts,
  selectedPostId,
  conflicts,
  onSelectPost,
  onDragStart,
  onDragEnd,
  onDropHour,
}: {
  day: CalendarDay;
  posts: ScheduleGridPost[];
  selectedPostId: number;
  conflicts: Record<number, string[]>;
  onSelectPost: (postId: number) => void;
  onDragStart: (postId: number) => void;
  onDragEnd: () => void;
  onDropHour: (dayKey: string, hour: number, postId?: number) => void;
}) {
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  return (
    <div className="relative min-w-0 border-r border-white/30 last:border-r-0">
      <div className={`sticky top-0 z-10 flex h-12 items-center justify-center border-b border-white/35 text-xs font-semibold text-slate-500 ${CAMPAIGN_SURFACE_BG}`}>
        {day.isToday ? (
          <span className="text-blue-600">
            {day.weekday}{' '}
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white">
              {day.dayNum}
            </span>
          </span>
        ) : (
          <span>
            {day.weekday} {day.dayNum}
          </span>
        )}
      </div>
      {SCHEDULE_GRID_HOURS.map((hour) => (
        <div
          key={`${day.key}-${hour}`}
          className="h-16 border-b border-white/25"
          onDragOver={handleDragOver}
          onDrop={(event) => {
            event.preventDefault();
            const postId = Number(event.dataTransfer.getData('text/post-id'));
            onDropHour(day.key, hour, postId || undefined);
          }}
        />
      ))}
      {posts.map((post) => (
        <SchedulePostCard
          key={post.id}
          post={post}
          isSelected={selectedPostId === post.id}
          hasConflict={Boolean(conflicts[post.id]?.length)}
          onSelect={() => onSelectPost(post.id)}
          onDragStart={() => onDragStart(post.id)}
          onDragEnd={onDragEnd}
        />
      ))}
    </div>
  );
}

function ScheduleGrid({
  scheduleDays,
  posts,
  slots,
  statusFilter,
  selectedPostId,
  conflicts,
  onSelectPost,
  onDragStart,
  onDragEnd,
  onDropHour,
}: {
  scheduleDays: CalendarDay[];
  posts: LinkedInPost[];
  slots: Record<number, CampaignScheduleSlot>;
  statusFilter: 'all' | CampaignScheduleStatus;
  selectedPostId: number;
  conflicts: Record<number, string[]>;
  onSelectPost: (postId: number) => void;
  onDragStart: (postId: number) => void;
  onDragEnd: () => void;
  onDropHour: (dayKey: string, hour: number, postId?: number) => void;
}) {
  const { t } = useUiLocale();
  const postsById = useMemo(() => new Map(posts.map((post) => [post.id, post])), [posts]);
  const visibleDayKeys = useMemo(() => new Set(scheduleDays.map((day) => day.key)), [scheduleDays]);

  const postsByDay = useMemo(() => {
    const grouped: Record<string, ScheduleGridPost[]> = {};
    for (const slot of Object.values(slots)) {
      if (!visibleDayKeys.has(slot.dayKey)) continue;
      const post = postsById.get(slot.postId);
      const template = getScheduleTemplate(slot.postId);
      const status = (post?.status ?? template?.status ?? 'Draft') as CampaignScheduleStatus;
      if (!matchesScheduleStatusFilter(status, statusFilter)) continue;

      const entry: ScheduleGridPost = {
        id: slot.postId,
        title: post?.title ?? template?.title ?? `Post ${slot.postId}`,
        subtitle: post?.preview ?? template?.subtitle ?? '',
        time: template?.time ?? scheduleHourLabel(slot.hour),
        status,
        hour: slot.hour,
      };
      (grouped[slot.dayKey] ??= []).push(entry);
    }
    return grouped;
  }, [postsById, slots, statusFilter, visibleDayKeys]);

  return (
    <div className={`relative min-h-0 flex-1 overflow-auto ${CAMPAIGN_SURFACE_BG} ${CAMPAIGN_SCROLLBAR}`}>
      <ScheduleLegendRow />
      <div className="relative min-h-[900px]">
        <div
          className="grid min-h-[900px]"
          style={{ gridTemplateColumns: `64px repeat(${scheduleDays.length}, minmax(112px, 1fr))` }}
        >
          <div className="border-r border-white/30 pt-12">
            <div className="h-12 border-b border-white/30 px-3 py-3 text-xs font-medium text-slate-400">{t('campaigns.allDay')}</div>
            {SCHEDULE_GRID_HOURS.map((hour) => (
              <div key={hour} className="h-16 border-b border-white/25 px-3 pt-2 text-[11px] text-slate-400">
                {scheduleHourLabel(hour)}
              </div>
            ))}
          </div>
          {scheduleDays.map((day) => (
            <ScheduleDayLane
              key={day.key}
              day={day}
              posts={postsByDay[day.key] ?? []}
              selectedPostId={selectedPostId}
              conflicts={conflicts}
              onSelectPost={onSelectPost}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDropHour={onDropHour}
            />
          ))}
        </div>
        <ScheduleCurrentTimeIndicator />
      </div>
    </div>
  );
}

function CampaignSchedulePanel({
  campaignName,
  posts,
  scheduleDays,
  slots,
  rangeLabel,
  start,
  end,
  timeZone,
  statusFilter,
  showUnscheduled,
  selectedPostId,
  conflicts,
  conflictCount,
  draggedPostId,
  onToday,
  onPrevWeek,
  onNextWeek,
  onTimeZoneChange,
  onStatusFilterChange,
  onShowUnscheduledChange,
  onSelectPost,
  onDragStart,
  onDragEnd,
  onDropHour,
}: {
  campaignName: string;
  posts: LinkedInPost[];
  scheduleDays: CalendarDay[];
  slots: Record<number, CampaignScheduleSlot>;
  rangeLabel: string;
  start: Date;
  end: Date;
  timeZone: StrategyTimeZoneId;
  statusFilter: 'all' | CampaignScheduleStatus;
  showUnscheduled: boolean;
  selectedPostId: number;
  conflicts: Record<number, string[]>;
  conflictCount: number;
  draggedPostId: number | null;
  onToday: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTimeZoneChange: (timeZone: StrategyTimeZoneId) => void;
  onStatusFilterChange: (value: 'all' | CampaignScheduleStatus) => void;
  onShowUnscheduledChange: (value: boolean) => void;
  onSelectPost: (postId: number) => void;
  onDragStart: (postId: number) => void;
  onDragEnd: () => void;
  onDropHour: (dayKey: string, hour: number, postId?: number) => void;
}) {
  const { t } = useUiLocale();
  const scheduledCount = Object.keys(slots).length;
  const unscheduledCount = posts.filter((post) => !slots[post.id]).length;

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${CAMPAIGN_SURFACE_BG}`}>
      <ScheduleToolbar
        rangeLabel={rangeLabel}
        start={start}
        end={end}
        timeZone={timeZone}
        statusFilter={statusFilter}
        showUnscheduled={showUnscheduled}
        onToday={onToday}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        onTimeZoneChange={onTimeZoneChange}
        onStatusFilterChange={onStatusFilterChange}
        onShowUnscheduledChange={onShowUnscheduledChange}
      />
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-white/25 px-4 py-2 text-[11px] text-slate-500">
        <span className="min-w-0 truncate whitespace-nowrap">
          {t('campaigns.postsOnCalendar', { name: campaignName, count: posts.length })}
        </span>
        <span className="shrink-0 whitespace-nowrap">
          {t('campaigns.scheduledUnscheduled', { scheduled: scheduledCount, unscheduled: unscheduledCount })}
          {draggedPostId != null && <span className="ml-2 text-blue-600">{t('campaigns.draggingPost')}</span>}
        </span>
      </div>
      <ScheduleGrid
        scheduleDays={scheduleDays}
        posts={posts}
        slots={slots}
        statusFilter={statusFilter}
        selectedPostId={selectedPostId}
        conflicts={conflicts}
        onSelectPost={onSelectPost}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDropHour={onDropHour}
      />
      <div className={`shrink-0 border-t border-white/30 px-5 py-3 ${CAMPAIGN_SURFACE_BG}`}>
        <div className={`flex items-start gap-3 rounded-xl px-3 py-2 text-xs ${conflictCount > 0 ? 'bg-red-50/90 text-red-800' : 'bg-blue-50/90 text-slate-600'}`}>
          {conflictCount > 0 ? (
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
          ) : (
            <Calendar className="h-4 w-4 shrink-0 text-blue-600" />
          )}
          <div>
            <p className="font-semibold">{conflictCount > 0 ? t('campaigns.schedulingConflicts', { count: conflictCount }) : t('campaigns.scheduleRules')}</p>
            <p>
              {t('campaigns.scheduleRulesDesc', {
                maxPerDay: CAMPAIGN_SCHEDULE_RULES.maxPostsPerDay,
                minHours: CAMPAIGN_SCHEDULE_RULES.minHoursBetween,
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentPostListItem({
  post,
  index,
  isSelected,
  onSelect,
}: {
  post: LinkedInPost;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { t } = useUiLocale();
  const label = post.title?.trim() || `Post ${index + 1}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative flex w-full items-start gap-3 overflow-hidden rounded-xl border px-3 py-3.5 text-left transition-all duration-200 ${
        isSelected ? SELECTED_POST_CARD : IDLE_POST_CARD
      }`}
    >
      {isSelected && (
        <span
          className="absolute left-0 w-1 rounded-none bg-gradient-to-b from-sky-400 via-blue-500 to-violet-500 shadow-[0_0_12px_rgba(59,130,246,0.65)]"
          aria-hidden
        />
      )}
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-none transition-all duration-200 ${
          isSelected
            ? 'bg-gradient-to-br from-blue-500 to-indigo-600 '
            : 'bg-gradient-to-br from-blue-50 to-sky-100/90 ring-1 ring-blue-100/90 group-hover:from-blue-100 group-hover:to-sky-100'
        }`}
      >
        <FileText className={`h-4 w-4 ${isSelected ? 'text-white' : 'text-blue-600'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`truncate text-sm font-semibold ${isSelected ? 'text-slate-900' : 'text-slate-800'}`}>
            {label}
          </p>
          <Badge className={`shrink-0 rounded-none border text-[10px] font-medium ${POST_STATUS_STYLE[post.status]}`}>
            {translatePostStatus(post.status, t)}
          </Badge>
        </div>
        <p className="mt-0.5 text-[11px] font-medium text-slate-400">{post.date}</p>
        <p className="mt-1.5 line-clamp-2 text-xs leading-snug text-slate-500">{post.preview}</p>
      </div>
    </button>
  );
}

function UnscheduledPostItem({
  post,
  isSelected,
  matchesFilter,
  onSelect,
  onDragStart,
  onDragEnd,
}: {
  post: LinkedInPost;
  isSelected: boolean;
  matchesFilter: boolean;
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  if (!matchesFilter) return null;

  const status = post.status as CampaignScheduleStatus;

  return (
    <button
      type="button"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/post-id', String(post.id));
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      className={`flex w-full items-start gap-2 rounded-none border px-2.5 py-2.5 text-left transition-all duration-200 ${
        isSelected ? SELECTED_POST_CARD : 'border-transparent hover:bg-white/50'
      }`}
    >
      <span className="pt-1 text-slate-300">::</span>
      <LinkedInLogo className="mt-0.5 h-4 w-4 object-contain" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-xs font-semibold text-slate-900">{post.title}</p>
          <Badge className={`shrink-0 border px-1.5 py-0 text-[9px] ${CAMPAIGN_SCHEDULE_STATUS_STYLE[status]}`}>
            {status}
          </Badge>
        </div>
        <p className="truncate text-[10px] text-slate-500">{post.preview}</p>
      </div>
    </button>
  );
}

function CampaignScheduleSidePanel({
  posts,
  slots,
  statusFilter,
  showUnscheduled,
  selectedPostId,
  conflicts,
  onSelectPost,
  onDragStart,
  onDragEnd,
  onUnscheduleDrop,
}: {
  posts: LinkedInPost[];
  slots: Record<number, CampaignScheduleSlot>;
  statusFilter: 'all' | CampaignScheduleStatus;
  showUnscheduled: boolean;
  selectedPostId: number;
  conflicts: Record<number, string[]>;
  onSelectPost: (postId: number) => void;
  onDragStart: (postId: number) => void;
  onDragEnd: () => void;
  onUnscheduleDrop: (postId: number) => void;
}) {
  const unscheduledPosts = posts.filter((post) => !slots[post.id]);
  const visibleUnscheduled = unscheduledPosts.filter((post) =>
    matchesScheduleStatusFilter(post.status as CampaignScheduleStatus, statusFilter),
  );

  const handlePanelDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  return (
    <>
      <div className={`flex-1 overflow-y-auto px-4 py-4 ${CAMPAIGN_SCROLLBAR}`}>
        {showUnscheduled && (
          <div
            className={`rounded-xl ${LIQUID_GLASS_CARD}`}
            onDragOver={handlePanelDragOver}
            onDrop={(event) => {
              event.preventDefault();
              const raw = event.dataTransfer.getData('text/post-id');
              const postId = Number(raw);
              if (postId) onUnscheduleDrop(postId);
            }}
          >
            <div className="flex items-center justify-between border-b border-white/35 px-3 py-3">
              <h4 className="text-sm font-semibold text-slate-900">
                Unscheduled Posts ({visibleUnscheduled.length})
              </h4>
            </div>
            <div className="divide-y divide-white/30">
              {visibleUnscheduled.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-slate-500">All posts are on the calendar.</p>
              ) : (
                visibleUnscheduled.map((post) => (
                  <UnscheduledPostItem
                    key={post.id}
                    post={post}
                    isSelected={selectedPostId === post.id}
                    matchesFilter
                    onSelect={() => onSelectPost(post.id)}
                    onDragStart={() => onDragStart(post.id)}
                    onDragEnd={onDragEnd}
                  />
                ))
              )}
            </div>
            <div className="m-3 rounded-xl border border-dashed border-white/50 px-4 py-5 text-center text-xs text-slate-500">
              <Calendar className="mx-auto mb-2 h-5 w-5 text-slate-300" />
              Drop here to unschedule
            </div>
          </div>
        )}

        {Object.keys(conflicts).length > 0 && (
          <div className={`mt-4 rounded-xl border border-red-200/80 bg-red-50/80 p-3 ${LIQUID_GLASS_CARD}`}>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-800">
              <AlertTriangle className="h-4 w-4" /> Conflicts
            </h4>
            <ul className="space-y-2 text-[11px] text-red-700">
              {Object.entries(conflicts).map(([postId, messages]) => (
                <li key={postId}>
                  <span className="font-semibold">Post {postId}:</span> {messages.join(' · ')}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`mt-4 rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
          <h4 className="mb-3 text-sm font-semibold text-slate-900">Status Legend</h4>
          {(['Draft', 'Scheduled', 'Approved', 'Posted'] as CampaignScheduleStatus[]).map((status) => (
            <div key={status} className="mb-3 flex items-start gap-2 last:mb-0">
              <span className={`mt-1 h-2 w-2 rounded-full ${CAMPAIGN_SCHEDULE_DOT_STYLE[status]}`} />
              <div>
                <p className="text-xs font-semibold text-slate-700">{status}</p>
                <p className="text-[11px] text-slate-500">
                  {status === 'Draft' && 'Content is being created'}
                  {status === 'Scheduled' && 'On the campaign calendar'}
                  {status === 'Approved' && 'Ready to be published'}
                  {status === 'Posted' && 'Published — locked on calendar'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-white/30 px-4 py-4">
        <div className={`rounded-xl p-4 text-xs text-slate-500 ${LIQUID_GLASS_CARD}`}>
          <Calendar className="mb-2 h-4 w-4 text-slate-400" />
          <p className="font-medium text-slate-600">Drag and drop to reschedule</p>
          <p className="mt-1">Click a calendar post to open it in the editor.</p>
        </div>
      </div>
    </>
  );
}

type AnalyticsMetric = {
  label: string;
  value: string;
  change: string;
  note: string;
  icon: LucideIcon;
  tone: string;
};

type AnalyticsPostStatus = 'Published' | 'Draft' | 'Scheduled' | 'Paused';
type AnalyticsPostFilter = 'all' | AnalyticsPostStatus;

type AnalyticsMetricType =
  | 'all'
  | 'impressions'
  | 'clicks'
  | 'reactions'
  | 'comments'
  | 'shares'
  | 'engagement'
  | 'ctr';

type AnalyticsPostRow = {
  title: string;
  preview: string;
  status: AnalyticsPostStatus;
  language: LanguageCode;
  dateKey: string | null;
  publishedAt: string;
  impressions: string;
  clicks: string;
  reactions: string;
  comments: string;
  shares: string;
  engagement: string;
  ctr: string;
};

type AnalyticsFilters = {
  rangeStart: Date;
  rangeEnd: Date;
  status: AnalyticsPostFilter;
  metricType: AnalyticsMetricType;
  language: 'all' | LanguageCode;
};

const ANALYTICS_DEFAULT_RANGE_START = startOfDay(new Date(2026, 4, 31));
const ANALYTICS_DEFAULT_RANGE_END = startOfDay(new Date(2026, 5, 6));

const ANALYTICS_METRIC_TYPE_OPTIONS: { value: AnalyticsMetricType; label: string }[] = [
  { value: 'all', label: 'All metrics' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'reactions', label: 'Reactions' },
  { value: 'comments', label: 'Comments' },
  { value: 'shares', label: 'Shares' },
  { value: 'engagement', label: 'Engagement rate' },
  { value: 'ctr', label: 'CTR' },
];

const ANALYTICS_TABLE_METRIC_COLUMNS: Record<Exclude<AnalyticsMetricType, 'all'>, string> = {
  impressions: 'Impressions',
  clicks: 'Clicks',
  reactions: 'Reactions',
  comments: 'Comments',
  shares: 'Shares',
  engagement: 'Engagement Rate',
  ctr: 'CTR',
};

const ANALYTICS_METRICS: AnalyticsMetric[] = [
  { label: 'Impressions', value: '125,430', change: '+18.6%', note: 'May 24 - May 30, 2026', icon: Eye, tone: 'text-blue-600 bg-blue-50' },
  { label: 'Clicks', value: '3,842', change: '+12.4%', note: 'May 24 - May 30, 2026', icon: Target, tone: 'text-blue-600 bg-blue-50' },
  { label: 'Reactions', value: '1,924', change: '+21.1%', note: 'May 24 - May 30, 2026', icon: Sparkles, tone: 'text-blue-600 bg-blue-50' },
  { label: 'Comments', value: '186', change: '+9.3%', note: 'May 24 - May 30, 2026', icon: MessageSquare, tone: 'text-purple-600 bg-purple-50' },
  { label: 'Shares', value: '342', change: '+11.7%', note: 'May 24 - May 30, 2026', icon: Send, tone: 'text-amber-600 bg-amber-50' },
  { label: 'Engagement Rate', value: '4.78%', change: '+3.2%', note: 'May 24 - May 30, 2026', icon: Hash, tone: 'text-emerald-600 bg-emerald-50' },
  { label: 'CTR', value: '3.07%', change: '-1.4%', note: 'May 24 - May 30, 2026', icon: Target, tone: 'text-cyan-600 bg-cyan-50' },
];

const ANALYTICS_NOTE_MONTH_TO_NUMBER: Record<string, string> = {
  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
};

function formatAnalyticsMetricNote(note: string): string {
  const match = note.match(/^(?:vs\s+)?(\w+)\s+(\d{1,2})\s*-\s*(\w+)\s+(\d{1,2})(?:,\s*\d{4})?$/);
  if (!match) return note;

  const [, startMonth, startDay, endMonth, endDay] = match;
  const startMonthNum = ANALYTICS_NOTE_MONTH_TO_NUMBER[startMonth] ?? '01';
  const endMonthNum = ANALYTICS_NOTE_MONTH_TO_NUMBER[endMonth] ?? '01';
  const padDay = (day: string) => day.padStart(2, '0');

  return `${padDay(startDay)}/${startMonthNum} - ${padDay(endDay)}/${endMonthNum}`;
}

const ANALYTICS_POST_ROWS: AnalyticsPostRow[] = [
  { title: 'Introducing Our New Feature', preview: 'Our new feature is designed to help...', status: 'Published', language: 'en', dateKey: '2026-05-20', publishedAt: 'May 20, 2026 10:00 AM', impressions: '28,456', clicks: '1,742', reactions: '842', comments: '73', shares: '156', engagement: '8.92%', ctr: '6.12%' },
  { title: 'Behind the Scenes of Our Team', preview: 'A day in the life of our amazing team...', status: 'Published', language: 'de', dateKey: '2026-05-23', publishedAt: 'May 23, 2026 09:30 AM', impressions: '19,872', clicks: '512', reactions: '421', comments: '31', shares: '78', engagement: '4.98%', ctr: '2.58%' },
  { title: 'Customer Success Story', preview: 'See how our solution helped...', status: 'Draft', language: 'vi', dateKey: '2026-05-26', publishedAt: 'May 26, 2026', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Industry Trends 2026', preview: 'Key insights and predictions for...', status: 'Scheduled', language: 'en', dateKey: '2026-05-29', publishedAt: 'May 29, 2026 11:00 AM', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Webinar Announcement', preview: 'Join us for an exclusive webinar on...', status: 'Paused', language: 'fr', dateKey: null, publishedAt: '-', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Productivity Tips for Teams', preview: 'Simple workflows your team can use...', status: 'Published', language: 'en', dateKey: '2026-05-31', publishedAt: 'May 31, 2026 09:00 AM', impressions: '16,410', clicks: '488', reactions: '302', comments: '19', shares: '44', engagement: '3.98%', ctr: '2.97%' },
  { title: 'New Analytics Dashboard', preview: 'A closer look at campaign insights...', status: 'Scheduled', language: 'de', dateKey: '2026-06-02', publishedAt: 'Jun 2, 2026 02:00 PM', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Webinar Recap: Future of AI', preview: 'Key moments from our latest webinar...', status: 'Draft', language: 'fr', dateKey: '2026-06-04', publishedAt: 'Jun 4, 2026', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Weekly Recap: Top Insights', preview: 'The most useful takeaways this week...', status: 'Published', language: 'vi', dateKey: '2026-06-05', publishedAt: 'Jun 5, 2026 01:00 PM', impressions: '12,938', clicks: '391', reactions: '244', comments: '12', shares: '39', engagement: '3.41%', ctr: '3.02%' },
  { title: 'Platform Update: Data Quality', preview: 'What changed in our data pipeline...', status: 'Scheduled', language: 'en', dateKey: '2026-06-06', publishedAt: 'Jun 6, 2026 10:00 AM', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Client Testimonial', preview: 'How a customer improved operations...', status: 'Draft', language: 'de', dateKey: '2026-06-07', publishedAt: 'Jun 7, 2026', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
  { title: 'Company Culture Highlight', preview: 'Meet the people behind the product...', status: 'Paused', language: 'en', dateKey: null, publishedAt: '-', impressions: '-', clicks: '-', reactions: '-', comments: '-', shares: '-', engagement: '-', ctr: '-' },
];

const ANALYTICS_STATUS_STYLE: Record<AnalyticsPostRow['status'], string> = {
  Published: 'bg-green-50 text-green-700 border-green-200',
  Draft: 'bg-blue-50 text-blue-700 border-blue-200',
  Scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  Paused: 'bg-red-50 text-red-700 border-red-200',
};

const ANALYTICS_PAGE_SIZE_OPTIONS = [5, 10, 12] as const;

function createDefaultAnalyticsFilters(): AnalyticsFilters {
  return {
    rangeStart: ANALYTICS_DEFAULT_RANGE_START,
    rangeEnd: ANALYTICS_DEFAULT_RANGE_END,
    status: 'all',
    metricType: 'all',
    language: 'all',
  };
}

function getAnalyticsCompareRangeLabel(rangeStart: Date, rangeEnd: Date, localeTag: string): string {
  const dayCount = Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86400000) + 1);
  const compareEnd = addDays(rangeStart, -1);
  const compareStart = addDays(compareEnd, -(dayCount - 1));
  return formatDateRangeLabel(compareStart, compareEnd, localeTag);
}

function isRowInAnalyticsDateRange(row: AnalyticsPostRow, rangeStart: Date, rangeEnd: Date): boolean {
  if (!row.dateKey) return false;
  const date = parseDateKey(row.dateKey);
  if (!date) return false;
  return isDateInCalendarRange(date, rangeStart, rangeEnd);
}

function filterAnalyticsRows(rows: AnalyticsPostRow[], filters: AnalyticsFilters): AnalyticsPostRow[] {
  return rows.filter((row) => {
    if (filters.status !== 'all' && row.status !== filters.status) return false;
    if (filters.language !== 'all' && row.language !== filters.language) return false;
    return isRowInAnalyticsDateRange(row, filters.rangeStart, filters.rangeEnd);
  });
}

function parseAnalyticsMetricValue(row: AnalyticsPostRow, metric: AnalyticsMetricType): number {
  if (metric === 'all') return 0;
  const raw = row[metric];
  if (raw === '-') return -1;
  const normalized = raw.replace(/,/g, '').replace('%', '');
  const value = Number(normalized);
  return Number.isNaN(value) ? -1 : value;
}

function sortAnalyticsRowsByMetric(rows: AnalyticsPostRow[], metric: AnalyticsMetricType): AnalyticsPostRow[] {
  if (metric === 'all') return rows;
  return [...rows].sort(
    (left, right) => parseAnalyticsMetricValue(right, metric) - parseAnalyticsMetricValue(left, metric),
  );
}

function isAnalyticsTableMetricColumn(head: string, metricType: AnalyticsMetricType): boolean {
  if (metricType === 'all') return false;
  return head === ANALYTICS_TABLE_METRIC_COLUMNS[metricType];
}

function analyticsMetricDataCellClass(head: string, metricType: AnalyticsMetricType, tone = ''): string {
  const highlight = isAnalyticsTableMetricColumn(head, metricType);
  return `max-w-[7.5rem] truncate whitespace-nowrap px-3 py-2 ${tone} ${
    highlight ? 'bg-blue-50/70 font-semibold text-blue-900' : ''
  }`.trim();
}

const ANALYTICS_DISPLAY_MAX_LEN = 8;

function truncateAnalyticsText(value: string, maxLength = ANALYTICS_DISPLAY_MAX_LEN): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength)}...`;
}

function AnalyticsTruncatedText({
  value,
  className = '',
  maxLength = ANALYTICS_DISPLAY_MAX_LEN,
}: {
  value: string;
  className?: string;
  maxLength?: number;
}) {
  const trimmed = value.trim();
  const display = truncateAnalyticsText(trimmed, maxLength);

  return (
    <span className={`block min-w-0 truncate whitespace-nowrap ${className}`} title={trimmed}>
      {display}
    </span>
  );
}

function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function exportAnalyticsRows(rows: AnalyticsPostRow[]) {
  if (typeof document === 'undefined') return;

  const headers = ['Post', 'Status', 'Language', 'Published At', 'Impressions', 'Clicks', 'Reactions', 'Comments', 'Shares', 'Engagement Rate', 'CTR'];
  const csvRows = rows.map((row) => [
    row.title,
    row.status,
    LANGUAGE_OPTIONS[row.language].label,
    row.publishedAt,
    row.impressions,
    row.clicks,
    row.reactions,
    row.comments,
    row.shares,
    row.engagement,
    row.ctr,
  ]);
  const csv = [headers, ...csvRows].map((cells) => cells.map(escapeCsvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'linkedin-post-performance.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

function getAnalyticsPageRows(
  rows: AnalyticsPostRow[],
  page: number,
  pageSize: number,
): AnalyticsPostRow[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

function AnalyticsFilterSelect({
  label,
  value,
  onValueChange,
  children,
  triggerClassName,
  open,
  onOpenChange,
  onSelectContentOutside,
  onTriggerPointerDown,
  exclusiveSelectId,
  exclusiveSelectTriggerClass,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectContentOutside?: (event: Event) => void;
  onTriggerPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  exclusiveSelectId?: string;
  exclusiveSelectTriggerClass?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
        <SelectTrigger
          data-exclusive-select-id={exclusiveSelectId}
          onPointerDown={onTriggerPointerDown}
          className={`${exclusiveSelectTriggerClass ?? ''} ${triggerClassName ?? 'h-8 w-[132px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600'}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent onPointerDownOutside={onSelectContentOutside}>{children}</SelectContent>
      </Select>
    </div>
  );
}

function AnalyticsHeader({
  filters,
  rangeLabel,
  compareLabel,
  resultCount,
  onFiltersChange,
  onResetFilters,
  onSync,
}: {
  filters: AnalyticsFilters;
  rangeLabel: string;
  compareLabel: string;
  resultCount: number;
  onFiltersChange: (patch: Partial<AnalyticsFilters>) => void;
  onResetFilters: () => void;
  onSync: () => void;
}) {
  const { t } = useUiLocale();
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'dateRange' | 'status' | 'metricType' | 'language'>('analytics-header');

  return (
    <div className={`flex shrink-0 flex-col gap-3 border-b border-white/30 px-4 py-3 ${LIQUID_GLASS_SOFT}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="whitespace-nowrap text-xs text-slate-500">
            Compare: <span className="font-semibold text-blue-600">{compareLabel}</span>
          </span>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
            {resultCount} post{resultCount === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onResetFilters}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </Button>
          <Button size="sm" className="h-8 gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700" onClick={onSync}>
            <RotateCcw className="h-3.5 w-3.5" /> Sync Analytics
          </Button>
        </div>
      </div>
      <div
        className="flex flex-wrap items-end gap-3"
        data-exclusive-select-group={exclusiveSelectGroupId}
      >
        <CampaignDateRangePicker
          start={filters.rangeStart}
          end={filters.rangeEnd}
          label={rangeLabel}
          onRangeChange={(rangeStart, rangeEnd) => onFiltersChange({ rangeStart, rangeEnd })}
          open={openSelect === 'dateRange'}
          exclusiveSelectId="dateRange"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('dateRange')}
          onTriggerPointerDown={onTriggerPointerDown('dateRange')}
          triggerClassName="flex items-center gap-2 rounded-lg border border-white/45 bg-white/45 px-3 py-2 text-xs font-medium text-slate-700 backdrop-blur-md transition-colors hover:bg-white/60"
        />
        <AnalyticsFilterSelect
          label="Post status"
          value={filters.status}
          open={openSelect === 'status'}
          exclusiveSelectId="status"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('status')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('status')}
          onValueChange={(value) => {
            onFiltersChange({ status: value as AnalyticsPostFilter });
            setOpenSelect(null);
          }}
        >
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="Published">Published</SelectItem>
          <SelectItem value="Draft">Draft</SelectItem>
          <SelectItem value="Scheduled">Scheduled</SelectItem>
          <SelectItem value="Paused">Paused</SelectItem>
        </AnalyticsFilterSelect>
        <AnalyticsFilterSelect
          label="Metric type"
          value={filters.metricType}
          open={openSelect === 'metricType'}
          exclusiveSelectId="metricType"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('metricType')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('metricType')}
          onValueChange={(value) => {
            onFiltersChange({ metricType: value as AnalyticsMetricType });
            setOpenSelect(null);
          }}
          triggerClassName="h-8 w-[148px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600"
        >
          {ANALYTICS_METRIC_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </AnalyticsFilterSelect>
        <AnalyticsFilterSelect
          label={t('campaigns.language')}
          value={filters.language}
          open={openSelect === 'language'}
          exclusiveSelectId="language"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('language')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('language')}
          onValueChange={(value) => {
            onFiltersChange({ language: value as AnalyticsFilters['language'] });
            setOpenSelect(null);
          }}
          triggerClassName="h-8 w-[156px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600"
        >
          <SelectItem value="all">All languages</SelectItem>
          {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
            <SelectItem key={code} value={code}>
              <span className="flex items-center gap-2">
                <LanguageFlagByCode code={code} />
                {LANGUAGE_OPTIONS[code].label}
              </span>
            </SelectItem>
          ))}
        </AnalyticsFilterSelect>
      </div>
    </div>
  );
}

function AnalyticsMetricCard({ metric, highlighted = false }: { metric: AnalyticsMetric; highlighted?: boolean }) {
  const Icon = metric.icon;
  const isNegative = metric.change.startsWith('-');

  return (
    <div
      className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD} ${
        highlighted ? 'ring-2 ring-blue-300/80' : ''
      }`}
    >
      <div className="mb-3 flex min-w-0 items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${metric.tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <AnalyticsTruncatedText value={metric.label} className="text-xs font-semibold text-slate-700" maxLength={24} />
      </div>
      <div className="flex min-w-0 items-end gap-2">
        <AnalyticsTruncatedText value={metric.value} className="text-xl font-bold text-slate-900" />
        <span
          className={`shrink-0 whitespace-nowrap pb-1 text-xs font-semibold ${
            isNegative ? 'text-red-600' : 'text-green-600'
          }`}
        >
          {metric.change}
        </span>
      </div>
      <AnalyticsTruncatedText value={formatAnalyticsMetricNote(metric.note)} className="mt-1 text-[10px] text-slate-400" maxLength={32} />
    </div>
  );
}

function analyticsMetricMatchesFilter(label: string, metricType: AnalyticsMetricType): boolean {
  if (metricType === 'all') return true;
  const labelByMetric: Record<Exclude<AnalyticsMetricType, 'all'>, string> = {
    impressions: 'Impressions',
    clicks: 'Clicks',
    reactions: 'Reactions',
    comments: 'Comments',
    shares: 'Shares',
    engagement: 'Engagement Rate',
    ctr: 'CTR',
  };
  return label === labelByMetric[metricType];
}

function AnalyticsMetricGrid({ metricType }: { metricType: AnalyticsMetricType }) {
  const visibleMetrics = ANALYTICS_METRICS.filter((metric) => analyticsMetricMatchesFilter(metric.label, metricType));
  const columnClass = metricType === 'all' ? 'grid-cols-7' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid min-w-0 items-stretch gap-3 ${columnClass}`}>
      {visibleMetrics.map((metric) => (
        <div key={metric.label} className="min-w-0">
          <AnalyticsMetricCard metric={metric} highlighted={metricType !== 'all'} />
        </div>
      ))}
    </div>
  );
}

function AnalyticsLineChart() {
  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <div className="mb-3 flex min-w-0 items-center justify-between gap-2">
        <h4 className="min-w-0 truncate whitespace-nowrap text-sm font-semibold text-slate-900">
          Performance Over Time
        </h4>
        <button
          type="button"
          className="shrink-0 whitespace-nowrap rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-500"
        >
          Daily v
        </button>
      </div>
      <svg viewBox="0 0 520 220" className="h-[220px] min-w-0 w-full">
        {[40, 80, 120, 160, 200].map((y) => (
          <line key={y} x1="40" x2="500" y1={y} y2={y} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        <polyline fill="none" stroke="#2563eb" strokeWidth="3" points="40,150 115,132 190,122 265,75 340,110 420,120 500,118" />
        <polyline fill="none" stroke="#22c55e" strokeWidth="3" points="40,185 115,175 190,170 265,160 340,168 420,172 500,175" />
        <polyline fill="none" stroke="#8b5cf6" strokeWidth="3" points="40,170 115,148 190,145 265,112 340,130 420,142 500,145" />
        {['May 31', 'Jun 1', 'Jun 2', 'Jun 3', 'Jun 4', 'Jun 5', 'Jun 6'].map((label, index) => (
          <text key={label} x={40 + index * 76} y="214" textAnchor="middle" className="fill-slate-400 text-[10px]">{label}</text>
        ))}
      </svg>
    </div>
  );
}

const ANALYTICS_DONUT_LEGEND = [
  { color: 'bg-blue-600', label: 'Reactions 78.6% (1,924)' },
  { color: 'bg-purple-500', label: 'Comments 7.6% (186)' },
  { color: 'bg-amber-500', label: 'Shares 13.9% (342)' },
] as const;

function AnalyticsDonutCard() {
  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-4 truncate whitespace-nowrap text-sm font-semibold text-slate-900">Engagement Breakdown</h4>
      <div className="flex min-w-0 items-center justify-center gap-4">
        <div
          className="relative h-36 w-36 shrink-0 rounded-full"
          style={{ background: 'conic-gradient(#2563eb 0 64%, #8b5cf6 64% 82%, #f59e0b 82% 100%)' }}
        >
          <div className="absolute inset-5 flex min-w-0 flex-col items-center justify-center rounded-full bg-white/85 px-1 backdrop-blur-md">
            <span className="whitespace-nowrap text-xs text-slate-400">Total</span>
            <AnalyticsTruncatedText value="2,452" className="text-xl font-bold text-slate-900" />
          </div>
        </div>
        <div className="min-w-0 space-y-3 text-xs">
          {ANALYTICS_DONUT_LEGEND.map((item) => (
            <p key={item.label} className="flex min-w-0 items-center whitespace-nowrap">
              <span className={`mr-2 inline-block h-2 w-2 shrink-0 rounded-full ${item.color}`} />
              <AnalyticsTruncatedText value={item.label} maxLength={28} />
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalyticsTopPostCard() {
  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-4 truncate whitespace-nowrap text-sm font-semibold text-slate-900">Top Performing Post</h4>
      <div className="flex min-w-0 gap-3">
        <div className="h-16 w-16 shrink-0 rounded-lg bg-slate-800" />
        <div className="min-w-0 flex-1">
          <AnalyticsTruncatedText
            value="Introducing Our New Feature"
            className="text-sm font-semibold text-slate-900"
            maxLength={24}
          />
          <p className="truncate whitespace-nowrap text-xs text-slate-500">May 20, 2026 - 10:00 AM</p>
          <Badge className="mt-1 shrink-0 whitespace-nowrap border-green-200 bg-green-50 text-green-700">Published</Badge>
        </div>
      </div>
      <div className="mt-4 space-y-2 text-xs text-slate-600">
        <p className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap">
          <span className="shrink-0 whitespace-nowrap">Impressions</span>
          <AnalyticsTruncatedText value="28,456" className="text-xl font-bold text-slate-900" />
        </p>
        <p className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap">
          <span className="shrink-0 whitespace-nowrap">Engagement Rate</span>
          <AnalyticsTruncatedText value="8.92%" className="text-xl font-bold text-slate-900" />
        </p>
        <p className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap">
          <span className="shrink-0 whitespace-nowrap">Clicks</span>
          <AnalyticsTruncatedText value="1,742" className="text-xl font-bold text-slate-900" />
        </p>
      </div>
      <Button variant="outline" size="sm" className="mt-auto w-full truncate text-xs">
        View Post Analytics
      </Button>
    </div>
  );
}

const ANALYTICS_TABLE_HEADERS = [
  'Post',
  'Status',
  'Language',
  'Published At',
  'Impressions',
  'Clicks',
  'Reactions',
  'Comments',
  'Shares',
  'Engagement Rate',
  'CTR',
  'Actions',
] as const;

function AnalyticsPostPerformanceTable({
  rows,
  filters,
  onFiltersChange,
}: {
  rows: AnalyticsPostRow[];
  filters: AnalyticsFilters;
  onFiltersChange: (patch: Partial<AnalyticsFilters>) => void;
}) {
  const { t, localeTag } = useUiLocale();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof ANALYTICS_PAGE_SIZE_OPTIONS)[number]>(5);
  const [selectedRow, setSelectedRow] = useState<AnalyticsPostRow | null>(null);
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'dateRange' | 'status' | 'metricType' | 'language'>('analytics-table-filters');

  useEffect(() => {
    setPage(1);
    setSelectedRow(null);
  }, [filters.rangeStart, filters.rangeEnd, filters.status, filters.metricType, filters.language, rows.length]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = getAnalyticsPageRows(rows, safePage, pageSize);
  const showStart = rows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showEnd = Math.min(safePage * pageSize, rows.length);

  return (
    <div className={`flex flex-col rounded-xl ${LIQUID_GLASS_CARD}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Post Performance</h4>
          <p className="text-[11px] text-slate-500">Filtered by header controls above</p>
        </div>
        <div
          className="flex flex-wrap items-end gap-2"
          data-exclusive-select-group={exclusiveSelectGroupId}
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Date range</span>
            <CampaignDateRangePicker
              start={filters.rangeStart}
              end={filters.rangeEnd}
              label={formatDateRangeLabel(filters.rangeStart, filters.rangeEnd, localeTag)}
              onRangeChange={(rangeStart, rangeEnd) => onFiltersChange({ rangeStart, rangeEnd })}
              open={openSelect === 'dateRange'}
              exclusiveSelectId="dateRange"
              exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
              onOpenChange={onOpenChange('dateRange')}
              onTriggerPointerDown={onTriggerPointerDown('dateRange')}
              triggerClassName="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white/60 px-2.5 text-xs text-slate-600 hover:bg-white/80"
            />
          </div>
          <AnalyticsFilterSelect
            label="Status"
            value={filters.status}
            open={openSelect === 'status'}
            exclusiveSelectId="status"
            exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
            onOpenChange={onOpenChange('status')}
            onSelectContentOutside={onSelectContentOutside}
            onTriggerPointerDown={onTriggerPointerDown('status')}
            onValueChange={(value) => {
              onFiltersChange({ status: value as AnalyticsPostFilter });
              setOpenSelect(null);
            }}
            triggerClassName="h-8 w-[120px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600"
          >
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Published">Published</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Paused">Paused</SelectItem>
          </AnalyticsFilterSelect>
          <AnalyticsFilterSelect
            label="Metric"
            value={filters.metricType}
            open={openSelect === 'metricType'}
            exclusiveSelectId="metricType"
            exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
            onOpenChange={onOpenChange('metricType')}
            onSelectContentOutside={onSelectContentOutside}
            onTriggerPointerDown={onTriggerPointerDown('metricType')}
            onValueChange={(value) => {
              onFiltersChange({ metricType: value as AnalyticsMetricType });
              setOpenSelect(null);
            }}
            triggerClassName="h-8 w-[132px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600"
          >
            {ANALYTICS_METRIC_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </AnalyticsFilterSelect>
          <AnalyticsFilterSelect
            label={t('campaigns.language')}
            value={filters.language}
            open={openSelect === 'language'}
            exclusiveSelectId="language"
            exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
            onOpenChange={onOpenChange('language')}
            onSelectContentOutside={onSelectContentOutside}
            onTriggerPointerDown={onTriggerPointerDown('language')}
            onValueChange={(value) => {
              onFiltersChange({ language: value as AnalyticsFilters['language'] });
              setOpenSelect(null);
            }}
            triggerClassName="h-8 w-[140px] rounded-lg border-slate-200 bg-white/60 text-xs text-slate-600"
          >
            <SelectItem value="all">All languages</SelectItem>
            {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
              <SelectItem key={code} value={code}>{LANGUAGE_OPTIONS[code].label}</SelectItem>
            ))}
          </AnalyticsFilterSelect>
          <button
            type="button"
            onClick={() => exportAnalyticsRows(rows)}
            className="mb-0.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-white/70 hover:text-blue-600"
          >
            Export
          </button>
        </div>
      </div>
      <div className={`overflow-x-auto ${CAMPAIGN_SCROLLBAR}`}>
        <table className="w-full text-left text-xs">
          <thead className={`${CAMPAIGN_SURFACE_BG} text-slate-500`}>
            <tr>
              {ANALYTICS_TABLE_HEADERS.map((head) => (
                <th
                  key={head}
                  className={`whitespace-nowrap px-3 py-2 font-semibold ${
                    isAnalyticsTableMetricColumn(head, filters.metricType) ? 'bg-blue-100/70 text-blue-800' : ''
                  }`}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y divide-slate-100 ${CAMPAIGN_SURFACE_BG}`}>
            {pageRows.map((row) => (
              <tr key={row.title} className="hover:bg-slate-50/70">
                <td className="min-w-[220px] px-3 py-2">
                  <p className="font-semibold text-slate-900">{row.title}</p>
                  <p className="truncate text-[11px] text-slate-500">{row.preview}</p>
                </td>
                <td className="px-3 py-2"><Badge className={`border text-[10px] ${ANALYTICS_STATUS_STYLE[row.status]}`}>{row.status}</Badge></td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <LanguageFlagByCode code={row.language} />
                    <span className="text-[11px]">{LANGUAGE_OPTIONS[row.language].label}</span>
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-500">{row.publishedAt}</td>
                <td className={analyticsMetricDataCellClass('Impressions', filters.metricType, 'text-blue-600')}>
                  <AnalyticsTruncatedText value={row.impressions} />
                </td>
                <td className={analyticsMetricDataCellClass('Clicks', filters.metricType, 'text-blue-600')}>
                  <AnalyticsTruncatedText value={row.clicks} />
                </td>
                <td className={analyticsMetricDataCellClass('Reactions', filters.metricType, 'text-green-600')}>
                  <AnalyticsTruncatedText value={row.reactions} />
                </td>
                <td className={analyticsMetricDataCellClass('Comments', filters.metricType)}>
                  <AnalyticsTruncatedText value={row.comments} />
                </td>
                <td className={analyticsMetricDataCellClass('Shares', filters.metricType, 'text-green-600')}>
                  <AnalyticsTruncatedText value={row.shares} />
                </td>
                <td className={analyticsMetricDataCellClass('Engagement Rate', filters.metricType, 'text-green-600')}>
                  <AnalyticsTruncatedText value={row.engagement} />
                </td>
                <td className={analyticsMetricDataCellClass('CTR', filters.metricType)}>
                  <AnalyticsTruncatedText value={row.ctr} />
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setSelectedRow(row)}
                    className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50"
                    aria-label={`View ${row.title}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-8 text-center text-slate-500">
                  No posts match the current date range, status, metric, or language filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selectedRow && (
        <div className="border-t border-slate-100 bg-blue-50/40 px-4 py-3 text-xs text-slate-600">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">{selectedRow.title}</p>
              <p className="mt-1 text-slate-500">{selectedRow.preview}</p>
            </div>
            <div className="grid shrink-0 grid-cols-3 gap-x-5 gap-y-1 text-right">
              <span>Impressions <strong className="text-slate-900">{selectedRow.impressions}</strong></span>
              <span>CTR <strong className="text-slate-900">{selectedRow.ctr}</strong></span>
              <span>Engagement <strong className="text-slate-900">{selectedRow.engagement}</strong></span>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
        <span>Showing {showStart} to {showEnd} of {rows.length} posts</span>
        <span className="flex items-center gap-2">
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => setPage(pageNumber)}
              className={`rounded px-2 py-1 transition-colors ${
                safePage === pageNumber ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {pageNumber}
            </button>
          ))}
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              setPageSize(Number(value) as (typeof ANALYTICS_PAGE_SIZE_OPTIONS)[number]);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-7 w-[92px] border-transparent bg-transparent px-1 text-xs text-slate-500 shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANALYTICS_PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>{size} / page</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </span>
      </div>
    </div>
  );
}

function CampaignAnalyticsPanel() {
  const { localeTag } = useUiLocale();
  const [filters, setFilters] = useState(createDefaultAnalyticsFilters);

  const updateFilters = useCallback((patch: Partial<AnalyticsFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const filteredRows = useMemo(() => {
    const filtered = filterAnalyticsRows(ANALYTICS_POST_ROWS, filters);
    return sortAnalyticsRowsByMetric(filtered, filters.metricType);
  }, [filters]);

  const rangeLabel = useMemo(
    () => formatDateRangeLabel(filters.rangeStart, filters.rangeEnd, localeTag),
    [filters.rangeStart, filters.rangeEnd, localeTag],
  );
  const compareLabel = useMemo(
    () => getAnalyticsCompareRangeLabel(filters.rangeStart, filters.rangeEnd, localeTag),
    [filters.rangeStart, filters.rangeEnd, localeTag],
  );

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${CAMPAIGN_SURFACE_BG}`}>
      <AnalyticsHeader
        filters={filters}
        rangeLabel={rangeLabel}
        compareLabel={compareLabel}
        resultCount={filteredRows.length}
        onFiltersChange={updateFilters}
        onResetFilters={() => setFilters(createDefaultAnalyticsFilters())}
        onSync={() => updateFilters({})}
      />
      <div className={`min-h-0 flex-1 space-y-4 overflow-y-auto p-4 ${CAMPAIGN_SCROLLBAR}`}>
        <AnalyticsMetricGrid metricType={filters.metricType} />
        <div className="grid min-w-0 grid-cols-12 items-stretch gap-4">
          <div className="col-span-4 h-full min-w-0">
            <AnalyticsLineChart />
          </div>
          <div className="col-span-4 h-full min-w-0">
            <AnalyticsDonutCard />
          </div>
          <div className="col-span-4 h-full min-w-0">
            <AnalyticsTopPostCard />
          </div>
        </div>
        <AnalyticsPostPerformanceTable
          rows={filteredRows}
          filters={filters}
          onFiltersChange={updateFilters}
        />
      </div>
    </div>
  );
}

function SettingsSection({
  icon: Icon,
  title,
  description,
  tone,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  tone: string;
  children: ReactNode;
}) {
  return (
    <div className={`grid h-full grid-cols-[220px_minmax(0,1fr)] overflow-hidden rounded-xl ${LIQUID_GLASS_CARD}`}>
      <div className={`border-r border-slate-100 p-4 ${CAMPAIGN_SURFACE_BG}`}>
        <div className="flex items-start gap-3">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone}`}>
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{description}</p>
          </div>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function SettingsTextInput({
  label,
  value,
  count,
}: {
  label: string;
  value: string;
  count?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">{label}</span>
      <div className="relative">
        <input
          defaultValue={value}
          className="h-9 w-full rounded-lg border border-white/45 bg-white/45 px-3 text-xs text-slate-700 backdrop-blur-md focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
        />
        {count && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{count}</span>}
      </div>
    </label>
  );
}

type SettingsSelectOption = {
  value: string;
  label: ReactNode;
};

function SettingsSelectField({
  label,
  value,
  onValueChange,
  options,
  open,
  onOpenChange,
  onSelectContentOutside,
  onTriggerPointerDown,
  exclusiveSelectId,
  exclusiveSelectTriggerClass,
}: {
  label: string;
  value: string;
  onValueChange?: (value: string) => void;
  options?: SettingsSelectOption[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectContentOutside?: (event: Event) => void;
  onTriggerPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  exclusiveSelectId?: string;
  exclusiveSelectTriggerClass?: string;
}) {
  if (!onValueChange) {
    return (
      <label className="block">
        <span className="mb-1.5 block text-xs font-semibold text-slate-700">{label}</span>
        <Select defaultValue={value} open={open} onOpenChange={onOpenChange}>
          <SelectTrigger
            data-exclusive-select-id={exclusiveSelectId}
            onPointerDown={onTriggerPointerDown}
            className={`${exclusiveSelectTriggerClass ?? ''} h-9 w-full border-white/45 bg-white/45 text-xs backdrop-blur-md`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent onPointerDownOutside={onSelectContentOutside}>
            <SelectItem value={value}>{value}</SelectItem>
          </SelectContent>
        </Select>
      </label>
    );
  }

  const items = options ?? [{ value, label: value }];

  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">{label}</span>
      <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
        <SelectTrigger
          data-exclusive-select-id={exclusiveSelectId}
          onPointerDown={onTriggerPointerDown}
          className={`${exclusiveSelectTriggerClass ?? ''} h-9 w-full border-white/45 bg-white/45 text-xs backdrop-blur-md`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent onPointerDownOutside={onSelectContentOutside}>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

function SettingsToggleSwitch({
  label,
  id,
  checked,
  defaultChecked,
  onChange,
}: {
  label: string;
  id?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  const inputId = id ?? `settings-toggle-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <label htmlFor={inputId} className="flex cursor-pointer items-center gap-2.5 text-xs text-slate-600">
      <span className="hawk-toggle-switch hawk-toggle-switch-sm shrink-0">
        <span className="switch">
          <input
            id={inputId}
            type="checkbox"
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange ? (event) => onChange(event.target.checked) : undefined}
          />
          <span className="slider" aria-hidden="true" />
        </span>
      </span>
      {label}
    </label>
  );
}

function SaveChangesButton({
  onClick,
  disabled,
  saving,
}: {
  onClick?: () => void;
  disabled?: boolean;
  saving?: boolean;
}) {
  const { t } = useUiLocale();
  return (
    <div className="flex justify-end">
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        onClick={onClick}
        disabled={disabled || saving}
      >
        {saving ? t('campaigns.settings.saving') : t('campaigns.settings.saveChanges')}
      </Button>
    </div>
  );
}

const SETTINGS_LANGUAGE_OPTIONS = (Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => ({
  value: code,
  label: (
    <span className="flex items-center gap-2">
      <LanguageFlagByCode code={code} />
      {LANGUAGE_OPTIONS[code].label}
    </span>
  ),
}));

function GeneralSettingsSection({
  campaignName,
  language,
  timeZone,
  isDirty,
  saveNotice,
  saving,
  onLanguageChange,
  onTimeZoneChange,
  onSave,
}: {
  campaignName: string;
  language: LanguageCode;
  timeZone: StrategyTimeZoneId;
  isDirty: boolean;
  saveNotice: string | null;
  saving: boolean;
  onLanguageChange: (language: LanguageCode) => void;
  onTimeZoneChange: (timeZone: StrategyTimeZoneId) => void;
  onSave: () => void;
}) {
  const { t } = useUiLocale();
  const settingsTimeZoneOptions = useMemo(
    () => STRATEGY_TIME_ZONE_ORDER.map((id) => ({
      value: id,
      label: getStrategyTimeZoneLabel(id, t),
    })),
    [t],
  );
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'language' | 'timeZone'>('settings-general');

  return (
    <SettingsSection
      icon={Settings}
      title={t('campaigns.settings.general')}
      description={t('campaigns.settings.generalDesc')}
      tone="bg-blue-50 text-blue-600"
    >
      <div className="grid grid-cols-2 gap-4" data-exclusive-select-group={exclusiveSelectGroupId}>
        <SettingsTextInput label={t('campaigns.settings.campaignName')} value={campaignName} count={`${campaignName.length} / 100`} />
        <div />
        <SettingsSelectField
          label={t('campaigns.settings.defaultLanguage')}
          value={language}
          open={openSelect === 'language'}
          exclusiveSelectId="language"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('language')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('language')}
          onValueChange={(value) => {
            onLanguageChange(value as LanguageCode);
            setOpenSelect(null);
          }}
          options={SETTINGS_LANGUAGE_OPTIONS}
        />
        <SettingsSelectField
          label={t('campaigns.settings.timeZone')}
          value={timeZone}
          open={openSelect === 'timeZone'}
          exclusiveSelectId="timeZone"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('timeZone')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('timeZone')}
          onValueChange={(value) => {
            onTimeZoneChange(value as StrategyTimeZoneId);
            setOpenSelect(null);
          }}
          options={settingsTimeZoneOptions}
        />
      </div>
      {saveNotice && <p className="mt-3 text-[11px] font-medium text-green-600">{saveNotice}</p>}
      <div className="mt-4">
        <SaveChangesButton onClick={onSave} disabled={!isDirty} saving={saving} />
      </div>
    </SettingsSection>
  );
}

function ApprovalWorkflowSection() {
  const { t } = useUiLocale();
  const {
    openId: openSelect,
    setOpenId: setOpenSelect,
    onOpenChange,
    onSelectContentOutside,
    onTriggerPointerDown,
    exclusiveSelectGroupId,
    exclusiveSelectTriggerClass,
  } = useExclusiveSelect<'reviewers' | 'approvers' | 'reminder'>('settings-approval');

  return (
    <SettingsSection
      icon={ShieldCheck}
      title={t('campaigns.settings.approval')}
      description={t('campaigns.settings.approvalDesc')}
      tone="bg-blue-50 text-blue-600"
    >
      <div className="grid grid-cols-2 gap-4" data-exclusive-select-group={exclusiveSelectGroupId}>
        <SettingsSelectField
          label={t('campaigns.settings.reviewers')}
          value="Marketing Team"
          open={openSelect === 'reviewers'}
          exclusiveSelectId="reviewers"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('reviewers')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('reviewers')}
        />
        <SettingsSelectField
          label={t('campaigns.settings.approvers')}
          value="Head of Marketing"
          open={openSelect === 'approvers'}
          exclusiveSelectId="approvers"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('approvers')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('approvers')}
        />
        <SettingsToggleSwitch label={t('campaigns.settings.autoApproveDrafts')} />
        <SettingsSelectField
          label={t('campaigns.settings.reminder')}
          value="24 hours before due date"
          open={openSelect === 'reminder'}
          exclusiveSelectId="reminder"
          exclusiveSelectTriggerClass={exclusiveSelectTriggerClass}
          onOpenChange={onOpenChange('reminder')}
          onSelectContentOutside={onSelectContentOutside}
          onTriggerPointerDown={onTriggerPointerDown('reminder')}
        />
      </div>
      <p className="mt-2 text-[11px] text-slate-400">{t('campaigns.settings.approvalNote')}</p>
      <div className="mt-4"><SaveChangesButton /></div>
    </SettingsSection>
  );
}

function NotificationsSection() {
  const { t } = useUiLocale();
  const items = [
    t('campaigns.settings.notifyApproval'),
    t('campaigns.settings.notifyReminders'),
    t('campaigns.settings.notifyPerformance'),
    t('campaigns.settings.notifyStatus'),
  ];

  return (
    <SettingsSection
      icon={Bell}
      title={t('campaigns.settings.notifications')}
      description={t('campaigns.settings.notificationsDesc')}
      tone="bg-blue-50 text-blue-600"
    >
      <div className="grid grid-cols-2 gap-3">
        {items.map((item, index) => (
          <SettingsToggleSwitch key={item} label={item} defaultChecked={index !== 2} />
        ))}
      </div>
      <div className="mt-4"><SaveChangesButton /></div>
    </SettingsSection>
  );
}

function IntegrationsSection() {
  const integrations = [
    { name: 'LinkedIn Company Page', note: 'DM Engineering', logo: 'in' },
    { name: 'Google Analytics', note: 'GA4 - DFM Engineering', logo: 'ga' },
  ];

  return (
    <SettingsSection
      icon={Link2}
      title="Integrations"
      description="Connect external tools and services."
      tone="bg-blue-50 text-blue-600"
    >
      <div className="space-y-2">
        {integrations.map((item) => (
          <div key={item.name} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${LIQUID_GLASS_SOFT}`}>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-xs font-bold text-blue-600">{item.logo}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-900">{item.name}</p>
              <p className="text-[11px] text-slate-500">{item.note}</p>
            </div>
            <Badge className="border-green-200 bg-green-50 text-[10px] text-green-700">Connected</Badge>
            <Button variant="outline" size="sm" className="h-7 text-xs">Manage</Button>
          </div>
        ))}
      </div>
      <div className="mt-4"><SaveChangesButton /></div>
    </SettingsSection>
  );
}

function PrivacySettingsSection() {
  return (
    <SettingsSection
      icon={Database}
      title="Data & Privacy"
      description="Manage data retention and privacy preferences."
      tone="bg-blue-50 text-blue-600"
    >
      <div className="grid grid-cols-2 gap-4">
        <SettingsSelectField label="Data Retention" value="12 months" />
        <div className="block">
          <span className="mb-2 block text-xs font-semibold text-slate-700">Anonymize Analytics Data</span>
          <SettingsToggleSwitch
            label="Remove personally identifiable information."
            defaultChecked
          />
        </div>
      </div>
      <div className="mt-4"><SaveChangesButton /></div>
    </SettingsSection>
  );
}

function DangerZoneSection() {
  return (
    <SettingsSection
      icon={AlertTriangle}
      title="Danger Zone"
      description="Irreversible and sensitive actions."
      tone="bg-red-50 text-red-600"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-900">Archive Campaign</p>
            <p className="text-[11px] text-slate-500">This will archive the campaign and all related content.</p>
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs">Archive</Button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-red-600">Delete Campaign</p>
            <p className="text-[11px] text-red-500">This action cannot be undone. All data will be permanently deleted.</p>
          </div>
          <Button variant="outline" size="sm" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50">Delete</Button>
        </div>
      </div>
    </SettingsSection>
  );
}

function CampaignSettingsPanel({
  campaignName,
  language,
  timeZone,
  isDirty,
  saveNotice,
  saving,
  onLanguageChange,
  onTimeZoneChange,
  onSaveGeneral,
}: {
  campaignName: string;
  language: LanguageCode;
  timeZone: StrategyTimeZoneId;
  isDirty: boolean;
  saveNotice: string | null;
  saving: boolean;
  onLanguageChange: (language: LanguageCode) => void;
  onTimeZoneChange: (timeZone: StrategyTimeZoneId) => void;
  onSaveGeneral: () => void;
}) {
  return (
    <div className={`min-h-0 flex-1 overflow-y-auto p-5 ${CAMPAIGN_SURFACE_BG} ${CAMPAIGN_SCROLLBAR}`}>
      <div className="space-y-4">
        <GeneralSettingsSection
          campaignName={campaignName}
          language={language}
          timeZone={timeZone}
          isDirty={isDirty}
          saveNotice={saveNotice}
          saving={saving}
          onLanguageChange={onLanguageChange}
          onTimeZoneChange={onTimeZoneChange}
          onSave={onSaveGeneral}
        />
        <ApprovalWorkflowSection />
        <NotificationsSection />
        <IntegrationsSection />
        <PrivacySettingsSection />
        <DangerZoneSection />
      </div>
    </div>
  );
}

type OverviewInfoCard = {
  icon: LucideIcon;
  title: string;
  value: string;
  detail: string;
  tone: string;
};

const OVERVIEW_CAMPAIGN_PERIOD = {
  start: new Date(2026, 4, 12),
  end: new Date(2026, 5, 30),
  daysRemaining: 50,
};

const OVERVIEW_LAST_UPDATED = new Date(2026, 4, 31);

const OVERVIEW_INFO_CARDS: Array<Omit<OverviewInfoCard, 'title'> & { titleKey: string }> = [
  { icon: CheckCircle2, titleKey: 'campaigns.overview.campaignStatus', value: 'Active', detail: '', tone: 'bg-green-50 text-green-600' },
  { icon: Target, titleKey: 'campaigns.overview.objective', value: 'Increase brand awareness and engagement among target audience.', detail: '', tone: 'bg-blue-50 text-blue-600' },
  { icon: Users, titleKey: 'campaigns.overview.targetAudience', value: 'Marketing professionals, Business owners, SaaS enthusiasts', detail: '', tone: 'bg-purple-50 text-purple-600' },
  { icon: Tag, titleKey: 'campaigns.overview.category', value: 'SaaS / Technology', detail: '', tone: 'bg-cyan-50 text-cyan-600' },
  { icon: Users, titleKey: 'campaigns.overview.owner', value: 'Marketing Team', detail: 'members', tone: 'bg-slate-50 text-slate-600' },
];

const OVERVIEW_STATUS_ROWS = [
  { status: 'Draft' as PostStatus, value: 8, pct: '28.6%', color: 'bg-blue-500' },
  { status: 'Approved' as PostStatus, value: 7, pct: '25.0%', color: 'bg-green-500' },
  { status: 'Scheduled' as PostStatus, value: 6, pct: '21.4%', color: 'bg-amber-500' },
  { status: 'Posted' as PostStatus, value: 5, pct: '17.9%', color: 'bg-slate-500' },
  { status: 'archived' as const, value: 1, pct: '3.6%', color: 'bg-red-500' },
];

const OVERVIEW_SCHEDULE = [
  { titleKey: 'campaigns.overview.nextPost' as const, date: new Date(2026, 5, 4, 9, 0), isTomorrow: true, post: 'Post 12 - Tips to Improve Productivity', color: 'bg-blue-50 text-blue-600' },
  { titleKey: 'campaigns.overview.following' as const, date: new Date(2026, 5, 6, 9, 0), isTomorrow: false, post: 'Post 16 - New Feature Spotlight', color: 'bg-green-50 text-green-600' },
  { titleKey: 'campaigns.overview.following' as const, date: new Date(2026, 5, 8, 1, 0), isTomorrow: false, post: 'Post 18 - Customer Success Story', color: 'bg-green-50 text-green-600' },
];

const OVERVIEW_ACTIVITY = [
  { labelKey: 'campaigns.overview.activity.postApproved', labelVars: { post: 11 }, date: new Date(2026, 4, 31, 14, 15), user: 'John Doe', color: 'bg-blue-50 text-blue-600' },
  { labelKey: 'campaigns.overview.activity.postScheduled', labelVars: { post: 12 }, date: new Date(2026, 4, 31, 10, 30), user: 'Jane Smith', color: 'bg-amber-50 text-amber-600' },
  { labelKey: 'campaigns.overview.activity.postPublished', labelVars: { post: 9 }, date: new Date(2026, 4, 30, 9, 5), user: 'System', color: 'bg-green-50 text-green-600' },
  { labelKey: 'campaigns.overview.activity.aiApplied', labelVars: { post: 14 }, date: new Date(2026, 4, 29, 16, 20), user: 'Marketing Team', color: 'bg-purple-50 text-purple-600' },
  { labelKey: 'campaigns.overview.activity.settingsUpdated', date: new Date(2026, 4, 29, 11, 45), user: 'John Doe', color: 'bg-red-50 text-red-600' },
];

function OverviewInfoCard({
  card,
  t,
  localeTag,
}: {
  card: (typeof OVERVIEW_INFO_CARDS)[number];
  t: TranslateFn;
  localeTag: string;
}) {
  const Icon = card.icon;
  const displayValue = card.titleKey === 'campaigns.overview.campaignStatus'
    ? t('campaigns.status.active')
    : card.value;
  let displayDetail = card.detail;
  if (card.titleKey === 'campaigns.overview.campaignStatus') {
    displayDetail = `${formatDateRangeLabel(OVERVIEW_CAMPAIGN_PERIOD.start, OVERVIEW_CAMPAIGN_PERIOD.end, localeTag)}\n${t('campaigns.overview.daysRemaining', { count: OVERVIEW_CAMPAIGN_PERIOD.daysRemaining })}`;
  } else if (card.detail === 'members') {
    displayDetail = t('campaigns.overview.members', { count: 3 });
  }

  return (
    <div className={`flex h-full flex-col rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${card.tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="text-xs font-semibold text-slate-700">{t(card.titleKey)}</p>
      </div>
      <p className="text-sm font-semibold text-slate-900">{displayValue}</p>
      {displayDetail && (
        <p className="mt-2 whitespace-pre-line text-[11px] leading-relaxed text-slate-500">{displayDetail}</p>
      )}
    </div>
  );
}

function OverviewInfoGrid() {
  const { t, localeTag } = useUiLocale();
  return (
    <div className="grid grid-cols-5 items-stretch gap-3">
      {OVERVIEW_INFO_CARDS.map((card) => (
        <OverviewInfoCard key={card.titleKey} card={card} t={t} localeTag={localeTag} />
      ))}
    </div>
  );
}

const OVERVIEW_NAV_LINK_CLASS =
  'text-left text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700';

function OverviewNavLink({
  label,
  onClick,
  className = 'mt-auto pt-4',
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`truncate whitespace-nowrap ${OVERVIEW_NAV_LINK_CLASS} ${className}`}
      title={label}
    >
      {label}
    </button>
  );
}

function PostStatusSummaryCard({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useUiLocale();
  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-4 truncate whitespace-nowrap text-sm font-semibold text-slate-900">{t('campaigns.overview.postStatusSummary')}</h4>
      <div className="flex min-w-0 items-center gap-4">
        <div
          className="relative h-32 w-32 shrink-0 rounded-full"
          style={{ background: 'conic-gradient(#3b82f6 0 29%, #22c55e 29% 54%, #f59e0b 54% 75%, #64748b 75% 93%, #ef4444 93% 100%)' }}
        >
          <div className="absolute inset-5 flex min-w-0 flex-col items-center justify-center rounded-full bg-white/85 px-1 backdrop-blur-md">
            <AnalyticsTruncatedText value="28" className="text-xl font-bold text-slate-900" />
            <span className="whitespace-nowrap text-[10px] text-slate-500">{t('campaigns.overview.totalPosts')}</span>
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          {OVERVIEW_STATUS_ROWS.map((row) => (
            <div key={row.status} className="flex min-w-0 items-center justify-between gap-2 text-xs whitespace-nowrap">
              <span className="flex min-w-0 items-center gap-2 text-slate-600">
                <span className={`h-2 w-2 shrink-0 rounded-full ${row.color}`} />
                <span className="truncate">
                  {row.status === 'archived'
                    ? t('campaigns.overview.archived')
                    : translatePostStatus(row.status, t)}
                </span>
              </span>
              <AnalyticsTruncatedText
                value={`${row.value} (${row.pct})`}
                className="shrink-0 font-medium text-slate-700"
                maxLength={14}
              />
            </div>
          ))}
        </div>
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewAllPosts')} onClick={onNavigate} />
    </div>
  );
}

function CampaignProgressCard({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useUiLocale();
  const progressStats = [
    ['28', t('campaigns.overview.totalPostsLabel')],
    ['7', translatePostStatus('Approved', t)],
    ['6', translatePostStatus('Scheduled', t)],
    ['5', translatePostStatus('Posted', t)],
  ] as const;

  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-4 truncate whitespace-nowrap text-sm font-semibold text-slate-900">{t('campaigns.overview.campaignProgress')}</h4>
      <AnalyticsTruncatedText value="64%" className="text-xl font-bold text-slate-900" />
      <p className="whitespace-nowrap text-xs text-slate-500">{t('campaigns.overview.completionRate')}</p>
      <div className="mt-4 h-2 rounded-full bg-slate-100">
        <div className="h-full w-[64%] rounded-full bg-blue-600" />
      </div>
      <div className="mt-6 grid min-w-0 grid-cols-4 gap-2 text-center">
        {progressStats.map(([value, label]) => (
          <div key={label} className="min-w-0">
            <AnalyticsTruncatedText value={value} className="text-xl font-bold text-slate-900" />
            <p className="truncate whitespace-nowrap text-[11px] text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewProgress')} onClick={onNavigate} className="mt-auto pt-5" />
    </div>
  );
}

function SchedulePreviewCard({ onNavigate }: { onNavigate: () => void }) {
  const { t, localeTag } = useUiLocale();

  const formatScheduleDetail = (item: (typeof OVERVIEW_SCHEDULE)[number]) => {
    const dateLabel = item.date.toLocaleDateString(localeTag, {
      weekday: item.isTomorrow ? undefined : 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = item.date.toLocaleTimeString(localeTag, { hour: 'numeric', minute: '2-digit' });
    const whenLabel = item.isTomorrow ? `${t('common.tomorrow')}, ${dateLabel}` : dateLabel;
    return `${whenLabel} - ${timeLabel}`;
  };

  return (
    <div className={`flex h-full min-w-0 flex-col overflow-hidden rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <div className="mb-4 flex min-w-0 items-center justify-between gap-2">
        <h4 className="min-w-0 truncate whitespace-nowrap text-sm font-semibold text-slate-900">{t('campaigns.overview.schedulePreview')}</h4>
        <button
          type="button"
          className="shrink-0 text-slate-400 transition-colors hover:text-blue-600"
          onClick={onNavigate}
          aria-label={t('campaigns.overview.openSchedule')}
        >
          &gt;
        </button>
      </div>
      <div className="space-y-3">
        {OVERVIEW_SCHEDULE.map((item) => (
          <div key={`${item.titleKey}-${item.date.toISOString()}`} className="flex min-w-0 gap-3">
            <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${item.color}`}>
              <Calendar className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate whitespace-nowrap text-xs font-semibold text-slate-900">{t(item.titleKey)}</p>
              <p className="truncate whitespace-nowrap text-[11px] text-slate-500">{formatScheduleDetail(item)}</p>
              <AnalyticsTruncatedText
                value={item.post}
                className="text-[11px] font-medium text-slate-700"
                maxLength={28}
              />
            </div>
          </div>
        ))}
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewSchedule')} onClick={onNavigate} />
    </div>
  );
}

function StrategySnapshotCard({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useUiLocale();
  const rows = [
    [t('campaigns.toneOfVoice'), 'Professional + Friendly + Confident'],
    [t('campaigns.contentPillars'), 'Product Education + Customer Success + Industry Insights'],
    [t('campaigns.keyMessages'), 'Simplify workflows + Drive growth + Trusted by experts'],
  ];

  return (
    <div className={`flex h-full flex-col rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-3 text-sm font-semibold text-slate-900">{t('campaigns.overview.strategySnapshot')}</h4>
      <div className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex gap-3 text-xs">
            <span className="w-28 shrink-0 font-semibold text-slate-600">{label}</span>
            <span className="min-w-0 truncate text-slate-500">{value}</span>
          </div>
        ))}
        <div className="flex gap-3 text-xs">
          <span className="w-28 shrink-0 font-semibold text-slate-600">{t('campaigns.overview.topHashtags')}</span>
          <span className="flex flex-wrap gap-1">
            {['#Innovation', '#ProductUpdate', '#Growth', t('campaigns.overview.moreHashtags', { count: 2 })].map((tag) => (
              <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{tag}</span>
            ))}
          </span>
        </div>
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewStrategy')} onClick={onNavigate} />
    </div>
  );
}

function AnalyticsSnapshotCard({ onNavigate }: { onNavigate: () => void }) {
  const { t, localeTag } = useUiLocale();
  const metrics = [
    [t('campaigns.overview.impressions'), '45,231', '+12.5%'],
    [t('campaigns.overview.clicks'), '1,234', '+8.3%'],
    [t('campaigns.overview.reactions'), '892', '+15.3%'],
    [t('campaigns.overview.comments'), '123', '+6.1%'],
    [t('campaigns.overview.shares'), '56', '+9.5%'],
    [t('campaigns.overview.engagementRate'), '3.12%', '+7.4%'],
  ];

  return (
    <div className={`flex h-full flex-col rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="text-sm font-semibold text-slate-900">{t('campaigns.overview.analyticsSnapshot')}</h4>
      <p className="mb-4 text-[11px] text-slate-400">
        {t('campaigns.overview.lastUpdated', {
          date: OVERVIEW_LAST_UPDATED.toLocaleDateString(localeTag, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        })}
      </p>
      <div className="grid grid-cols-6 gap-2">
        {metrics.map(([label, value, change]) => (
          <div key={label} className="rounded-lg bg-slate-50 p-2">
            <p className="text-[10px] text-slate-500">{label}</p>
            <p className="text-sm font-bold text-slate-900">{value}</p>
            <p className="text-[10px] font-semibold text-green-600">{change}</p>
          </div>
        ))}
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewAnalytics')} onClick={onNavigate} />
    </div>
  );
}

function RecentActivityCard({ onNavigate }: { onNavigate: () => void }) {
  const { t, localeTag } = useUiLocale();
  return (
    <div className={`flex h-full flex-col rounded-xl p-4 ${LIQUID_GLASS_CARD}`}>
      <h4 className="mb-3 text-sm font-semibold text-slate-900">{t('campaigns.overview.recentActivity')}</h4>
      <div className="divide-y divide-slate-100">
        {OVERVIEW_ACTIVITY.map((item) => (
          <div key={`${item.labelKey}-${item.date.toISOString()}`} className="flex items-center gap-3 py-2 text-xs">
            <span className={`flex h-6 w-6 items-center justify-center rounded-lg ${item.color}`}>
              <FileText className="h-3 w-3" />
            </span>
            <span className="flex-1 font-medium text-slate-700">
              {item.labelVars ? t(item.labelKey, item.labelVars) : t(item.labelKey)}
            </span>
            <span className="text-slate-400">{formatOverviewDateTime(item.date, localeTag)}</span>
            <Badge className="border-slate-200 bg-slate-50 text-[10px] text-slate-600">{item.user}</Badge>
          </div>
        ))}
      </div>
      <OverviewNavLink label={t('campaigns.overview.viewActivity')} onClick={onNavigate} className="mt-auto pt-3" />
    </div>
  );
}

function CampaignOverviewPanel({
  onNavigateTab,
}: {
  onNavigateTab: (tab: (typeof TABS)[number]) => void;
}) {
  return (
    <div className={`min-h-0 flex-1 overflow-y-auto p-4 ${CAMPAIGN_SURFACE_BG} ${CAMPAIGN_SCROLLBAR}`}>
      <div className="space-y-4">
        <OverviewInfoGrid />
        <div className="grid min-w-0 grid-cols-12 items-stretch gap-4">
          <div className="col-span-4 h-full min-w-0">
            <PostStatusSummaryCard onNavigate={() => onNavigateTab('Content')} />
          </div>
          <div className="col-span-4 h-full min-w-0">
            <CampaignProgressCard onNavigate={() => onNavigateTab('Analytics')} />
          </div>
          <div className="col-span-4 h-full min-w-0">
            <SchedulePreviewCard onNavigate={() => onNavigateTab('Schedule')} />
          </div>
        </div>
        <div className="grid grid-cols-12 items-stretch gap-4">
          <div className="col-span-6 h-full">
            <StrategySnapshotCard onNavigate={() => onNavigateTab('Strategy')} />
          </div>
          <div className="col-span-6 h-full">
            <AnalyticsSnapshotCard onNavigate={() => onNavigateTab('Analytics')} />
          </div>
        </div>
        <RecentActivityCard onNavigate={() => onNavigateTab('Content')} />
      </div>
    </div>
  );
}

function getNextPostId(campaigns: LinkedInCampaign[]): number {
  return campaigns.reduce((max, campaign) => {
    const campaignMax = campaign.posts.reduce((m, post) => Math.max(m, post.id), 0);
    return Math.max(max, campaignMax);
  }, 0) + 1;
}

function getNextCampaignId(campaigns: LinkedInCampaign[]): number {
  return campaigns.reduce((max, campaign) => Math.max(max, campaign.id), 0) + 1;
}

function getDefaultCampaignName(campaigns: LinkedInCampaign[]): string {
  const baseName = 'New Campaign';
  const existingNames = new Set(campaigns.map((campaign) => campaign.name));
  if (!existingNames.has(baseName)) return baseName;

  let index = 2;
  while (existingNames.has(`${baseName} ${index}`)) {
    index += 1;
  }
  return `${baseName} ${index}`;
}

function formatPostDate(date = new Date()): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseScheduleDate(dateLabel: string): Date | null {
  const date = new Date(dateLabel);
  if (Number.isNaN(date.getTime())) return null;
  return startOfDay(date);
}

export function CampaignsPage() {
  const { t, localeTag } = useUiLocale();
  const [campaigns, setCampaigns] = useState<LinkedInCampaign[]>(() =>
    MOCK_CAMPAIGNS.map((campaign) =>
      campaign.id === 1 ? { ...campaign, posts: buildBrandAwarenessQ2Posts() } : campaign,
    ),
  );
  const [selectedId, setSelectedId] = useState(1);
  const [selectedPostId, setSelectedPostId] = useState(1);
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>('Overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [language, setLanguage] = useState<LanguageCode>('en');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages] = useState<ChatMessage[]>(MOCK_CHAT);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const [scheduleWindowStart, setScheduleWindowStart] = useState(getInitialScheduleWindowStart);
  const [scheduleRangeEnd, setScheduleRangeEnd] = useState(getInitialScheduleWindowEnd);
  const [calendarPosts, setCalendarPosts] = useState<Record<string, CalendarPost[]>>(CALENDAR_POSTS);
  const [scheduleSlotsByCampaign, setScheduleSlotsByCampaign] = useState<
    Record<number, Record<number, CampaignScheduleSlot>>
  >(getInitialScheduleSlotsByCampaign);
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState<'all' | CampaignScheduleStatus>('all');
  const [showUnscheduledPanel, setShowUnscheduledPanel] = useState(true);
  const [draggedSchedulePostId, setDraggedSchedulePostId] = useState<number | null>(null);
  const [strategyDraft, setStrategyDraft] = useState<CampaignStrategyData>(createDefaultCampaignStrategy);
  const [strategyBaseline, setStrategyBaseline] = useState<CampaignStrategyData>(createDefaultCampaignStrategy);
  const [strategySavedAt, setStrategySavedAt] = useState<string | null>(null);
  const [strategyErrors, setStrategyErrors] = useState<Partial<Record<StrategyFieldKey, string>>>({});
  const [strategyAiPreview, setStrategyAiPreview] = useState<StrategyAiPreview | null>(null);
  const [strategyAiLoading, setStrategyAiLoading] = useState(false);
  const [strategySaving, setStrategySaving] = useState(false);
  const [strategySaveNotice, setStrategySaveNotice] = useState<string | null>(null);

  const scheduleDays = useMemo(
    () => buildCalendarDays(scheduleWindowStart, localeTag),
    [scheduleWindowStart, localeTag],
  );

  const scheduleRangeLabel = useMemo(
    () => formatDateRangeLabel(scheduleWindowStart, scheduleRangeEnd, localeTag),
    [scheduleRangeEnd, scheduleWindowStart, localeTag],
  );

  const goToToday = () => {
    const initial = getInitialCampaignScheduleRange();
    setScheduleWindowStart(initial.start);
    setScheduleRangeEnd(initial.end);
  };

  const goToCampaignScheduleWeek = () => {
    const window = getBrandAwarenessScheduleWindow();
    setScheduleWindowStart(window.start);
    setScheduleRangeEnd(window.end);
    writeSharedScheduleRange({ startKey: toDateKey(window.start), endKey: toDateKey(window.end) });
  };

  const shiftScheduleWeek = (delta: number) => {
    const nextStart = addDays(scheduleWindowStart, delta * SCHEDULE_WINDOW_DAYS);
    const nextEnd = addDays(scheduleRangeEnd, delta * SCHEDULE_WINDOW_DAYS);
    setScheduleWindowStart(nextStart);
    setScheduleRangeEnd(nextEnd);
    writeSharedScheduleRange({ startKey: toDateKey(nextStart), endKey: toDateKey(nextEnd) });
  };

  const handleMediaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) return;
    setMediaFiles((prev) => [...prev, ...files]);
    // Cho phép chọn lại cùng một file
    event.target.value = '';
  };

  const mediaPreviewUrls = useMemo(
    () => mediaFiles.map((file) => URL.createObjectURL(file)),
    [mediaFiles],
  );

  useEffect(() => {
    const syncSharedScheduleRange = () => {
      const sharedRange = readSharedScheduleRange();
      const sharedStart = sharedRange ? parseDateKey(sharedRange.startKey) : null;
      const sharedEnd = sharedRange ? parseDateKey(sharedRange.endKey) : null;
      if (!sharedStart || !sharedEnd) return;

      setScheduleWindowStart(sharedStart);
      setScheduleRangeEnd(sharedEnd);
    };

    window.addEventListener('storage', syncSharedScheduleRange);
    window.addEventListener('linkedin-schedule-range-updated', syncSharedScheduleRange);
    return () => {
      window.removeEventListener('storage', syncSharedScheduleRange);
      window.removeEventListener('linkedin-schedule-range-updated', syncSharedScheduleRange);
    };
  }, []);

  useEffect(() => {
    return () => {
      mediaPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [mediaPreviewUrls]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return campaigns.filter((c) => {
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.type.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [campaigns, searchQuery, statusFilter]);

  const selected = campaigns.find((c) => c.id === selectedId) ?? campaigns[0];
  const selectedPost = selected.posts.find((p) => p.id === selectedPostId);
  const postContentLength = selectedPost?.content.length ?? 0;
  const campaignScheduleSlots = scheduleSlotsByCampaign[selectedId] ?? {};
  const scheduleConflicts = useMemo(
    () => detectScheduleConflicts(campaignScheduleSlots, selected.posts, t),
    [campaignScheduleSlots, selected.posts, t],
  );
  const scheduleConflictCount = Object.keys(scheduleConflicts).length;

  useEffect(() => {
    const snapshot = readCampaignStrategySnapshot(selectedId);
    setStrategyDraft(snapshot.data);
    setStrategyBaseline(snapshot.data);
    setStrategySavedAt(snapshot.savedAt);
    setStrategyErrors({});
    setStrategyAiPreview(null);
    setStrategySaveNotice(null);
    setLanguage(snapshot.data.language);
  }, [selectedId]);

  const strategyIsDirty = useMemo(
    () => !strategiesAreEqual(strategyDraft, strategyBaseline),
    [strategyDraft, strategyBaseline],
  );

  const patchStrategyDraft = useCallback((patch: Partial<CampaignStrategyData>) => {
    setStrategyDraft((prev) => {
      const next = { ...prev, ...patch };
      if (patch.language && patch.timeZone === undefined) {
        next.timeZone = LANGUAGE_TIME_ZONES[patch.language];
      }
      return next;
    });
    if (patch.language) {
      setLanguage(patch.language);
    }
    setStrategyErrors((prev) => {
      const next = { ...prev };
      (Object.keys(patch) as StrategyFieldKey[]).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  }, []);

  const handleResetStrategy = useCallback(() => {
    setStrategyDraft(strategyBaseline);
    setStrategyErrors({});
    setStrategySaveNotice(null);
  }, [strategyBaseline]);

  const handleSaveStrategy = useCallback(() => {
    const errors = validateCampaignStrategy(strategyDraft, t);
    setStrategyErrors(errors);
    if (hasStrategyValidationErrors(errors)) return;

    setStrategySaving(true);
    const savedAt = writeCampaignStrategySnapshot(selectedId, strategyDraft);
    setStrategyBaseline(strategyDraft);
    setStrategySavedAt(savedAt);
    setStrategySaveNotice(t('campaigns.toastStrategySaved'));
    setStrategySaving(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('hawk-campaign-strategy-saved', { detail: { campaignId: selectedId } }),
      );
    }
  }, [selectedId, strategyDraft, t]);

  const handleGenerateStrategyAi = useCallback(async () => {
    setStrategyAiLoading(true);
    try {
      const preview = await generateStrategyAiPreview({
        id: selected.id,
        name: selected.name,
        type: selected.type,
        status: selected.status,
      });
      setStrategyAiPreview(preview);
    } finally {
      setStrategyAiLoading(false);
    }
  }, [selected]);

  const strategyFormProps = useMemo(
    () => ({
      data: strategyDraft,
      errors: strategyErrors,
      onChange: patchStrategyDraft,
    }),
    [strategyDraft, strategyErrors, patchStrategyDraft],
  );

  useEffect(() => {
    if (activeTab === 'Schedule' && selectedId === 1) {
      goToCampaignScheduleWeek();
    }
  }, [activeTab, selectedId]);

  const handleScheduleDragStart = useCallback((postId: number) => {
    setDraggedSchedulePostId(postId);
  }, []);

  const handleScheduleDragEnd = useCallback(() => {
    setDraggedSchedulePostId(null);
  }, []);

  const handleScheduleDrop = useCallback(
    (dayKey: string, hour: number, postIdFromEvent?: number) => {
      const postId = postIdFromEvent ?? draggedSchedulePostId;
      if (postId == null) return;

      const post = selected.posts.find((entry) => entry.id === postId);
      if (post?.status === 'Posted') {
        setDraggedSchedulePostId(null);
        return;
      }

      const targetDate = parseDateKey(dayKey);
      setScheduleSlotsByCampaign((prev) => ({
        ...prev,
        [selectedId]: {
          ...(prev[selectedId] ?? {}),
          [postId]: { postId, dayKey, hour },
        },
      }));

      if (targetDate) {
        setCampaigns((prev) =>
          prev.map((campaign) =>
            campaign.id !== selectedId
              ? campaign
              : {
                  ...campaign,
                  posts: campaign.posts.map((entry) =>
                    entry.id === postId
                      ? { ...entry, status: 'Scheduled', date: formatPostDate(targetDate) }
                      : entry,
                  ),
                },
          ),
        );

        if (post) {
          writeCampaignScheduleSharedPost({
            campaign: selected,
            post,
            dayKey,
            hour,
            languageLabel: LANGUAGE_OPTIONS[language].label,
          });
        }
      }

      setSelectedPostId(postId);
      setDraggedSchedulePostId(null);
    },
    [draggedSchedulePostId, language, selected, selected.posts, selectedId],
  );

  const handleUnscheduleDrop = useCallback(
    (postId: number) => {
      const post = selected.posts.find((entry) => entry.id === postId);
      if (post?.status === 'Posted') {
        setDraggedSchedulePostId(null);
        return;
      }

      setScheduleSlotsByCampaign((prev) => {
        const nextSlots = { ...(prev[selectedId] ?? {}) };
        delete nextSlots[postId];
        return { ...prev, [selectedId]: nextSlots };
      });

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id !== selectedId
            ? campaign
            : {
                ...campaign,
                posts: campaign.posts.map((entry) =>
                  entry.id === postId ? { ...entry, status: 'Draft', date: '—' } : entry,
                ),
              },
        ),
      );
      removeSharedScheduledPost(postId);
      setSelectedPostId(postId);
      setDraggedSchedulePostId(null);
    },
    [selected.posts, selectedId],
  );

  const handleSelectSchedulePost = useCallback((postId: number) => {
    setSelectedPostId(postId);
    setActiveTab('Content');
  }, []);

  const handleAddNewPost = () => {
    const savedStrategy = readCampaignStrategySnapshot(selectedId).data;
    const newPostId = getNextPostId(campaigns);
    const newPost: LinkedInPost = {
      id: newPostId,
      title: 'Untitled Post',
      date: formatPostDate(),
      status: 'Draft',
      preview: 'Start writing your post...',
      content: '',
      hashtags: savedStrategy.hashtagStrategy.slice(0, 5).join(' '),
    };

    setCampaigns((prev) =>
      prev.map((campaign) =>
        campaign.id === selectedId
          ? { ...campaign, posts: [...campaign.posts, newPost] }
          : campaign,
      ),
    );
    setSelectedPostId(newPostId);
    setMediaFiles([]);
  };

  const handleAddNewCampaign = () => {
    const newCampaignId = getNextCampaignId(campaigns);
    const newCampaign: LinkedInCampaign = {
      id: newCampaignId,
      name: getDefaultCampaignName(campaigns),
      type: 'General',
      status: 'Draft',
      language: LANGUAGE_OPTIONS[language].label,
      createdAt: formatPostDate(),
      posts: [],
    };

    setCampaigns((prev) => [...prev, newCampaign]);
    setSelectedId(newCampaignId);
    setSelectedPostId(0);
    setActiveTab('Content');
    setSearchQuery('');
    setStatusFilter('all');
    setMediaFiles([]);
  };

  const handleApproveAndSchedule = () => {
    if (!selectedPost) return;

    const scheduledDate = parseScheduleDate(selectedPost.date) ?? startOfDay(new Date());
    const dateKey = toDateKey(scheduledDate);
    const postIndex = selected.posts.findIndex((post) => post.id === selectedPost.id);
    const postLabel = selectedPost.title || `Post ${postIndex + 1}`;

    setCampaigns((prev) =>
      prev.map((campaign) =>
        campaign.id === selected.id
          ? {
              ...campaign,
              posts: campaign.posts.map((post) =>
                post.id === selectedPost.id ? { ...post, status: 'Scheduled' } : post,
              ),
            }
          : campaign,
      ),
    );

    setCalendarPosts((prev) => {
      const scheduledPosts = prev[dateKey] ?? [];
      const nextScheduledPosts = scheduledPosts.filter((post) => post.label !== postLabel);

      return {
        ...prev,
        [dateKey]: [
          ...nextScheduledPosts,
          { label: postLabel, time: DEFAULT_SCHEDULE_TIME, status: 'scheduled', check: true },
        ],
      };
    });

    setScheduleSlotsByCampaign((prev) => ({
      ...prev,
      [selected.id]: {
        ...(prev[selected.id] ?? {}),
        [selectedPost.id]: {
          postId: selectedPost.id,
          dayKey: dateKey,
          hour: parseScheduleHourLabel(DEFAULT_SCHEDULE_TIME),
        },
      },
    }));

    setScheduleWindowStart(addDays(scheduledDate, -3));

    upsertSharedScheduledPost({
      id: selectedPost.id,
      campaignId: selected.id,
      campaignName: selected.name,
      campaignType: selected.type,
      title: postLabel,
      description: selectedPost.content,
      dateKey,
      time: DEFAULT_SCHEDULE_TIME,
      status: 'Scheduled',
      language: LANGUAGE_OPTIONS[language].label,
    });
  };

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[rgb(224_231_255/0.6)]">
      <div className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute bottom-32 right-0 h-80 w-80 rounded-full bg-indigo-400/15 blur-3xl" aria-hidden />

      <div className="relative z-10 flex min-h-0 flex-1 overflow-hidden p-3 pb-4">
      {/* ── Top Row: 3 Panels ── */}
      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
      {/* ── Left Panel: LinkedIn List ── */}
      <div className={`flex w-[300px] shrink-0 flex-col overflow-hidden rounded-2xl ${GLASS_FRAME}`}>
        <div className="flex items-center justify-between border-b border-white/30 px-4 py-4">
          <h1 className="text-2xl font-bold tracking-tight text-slate-800 sm:text-3xl hawk-gradient-text">{t('campaigns.title')}</h1>
          <Button
            size="sm"
            className="gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700"
            onClick={handleAddNewCampaign}
          >
            <Plus className="h-3.5 w-3.5" /> {t('campaigns.newLinkedIn')}
          </Button>
        </div>

        <div className="space-y-2 border-b border-white/30 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={t('campaigns.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-full bg-white text-sm">
              <SelectValue placeholder={t('campaigns.allStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('campaigns.allStatus')}</SelectItem>
              <SelectItem value="Active">{translateLinkedInStatus('Active', t)}</SelectItem>
              <SelectItem value="Draft">{translateLinkedInStatus('Draft', t)}</SelectItem>
              <SelectItem value="Paused">{translateLinkedInStatus('Paused', t)}</SelectItem>
              <SelectItem value="Scheduled">{translateLinkedInStatus('Scheduled', t)}</SelectItem>
              <SelectItem value="Completed">{translateLinkedInStatus('Completed', t)}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className={`min-h-0 flex-1 overflow-y-auto ${CAMPAIGN_SCROLLBAR}`}>
          <AnimatePresence initial={false}>
            {filtered.map((c) => (
              <motion.button
                key={c.id}
                type="button"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                onClick={() => { setSelectedId(c.id); setSelectedPostId(c.posts[0]?.id ?? 0); }}
                className={`flex  w-full items-center gap-3 border-b border-white/20 px-4 py-3 text-left transition-colors ${
                  selectedId === c.id
                    ? 'border-l-2 border-l-blue-600 bg-slate-300/55'
                    : 'hover:bg-slate-400/35'
                }`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                  <LinkedInLogo className="h-7 w-7 object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{c.name}</p>
                  <p className="truncate text-[11px] text-slate-500">{translateCampaignType(c.type, t)}</p>
                </div>
                <Badge className={`shrink-0 border text-[10px] font-medium ${STATUS_STYLE[c.status]}`}>
                  {translateLinkedInStatus(c.status, t)}
                </Badge>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        <div className="flex shrink-0 items-center border-t border-white/30 px-4 py-2.5">
          <p className="text-[11px] text-slate-500">
            {t('campaigns.campaignCount', { count: filtered.length })}
          </p>
        </div>
      </div>

      {/* ── Center Panel: LinkedIn Detail ── */}
      <div className={`flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl ${LIQUID_GLASS_PANEL}`}>
        {/* Header */}
        <div className="shrink-0 border-b border-white/30 px-4 py-4">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-slate-900">{selected.name}</h2>
                {selectedPost ? (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${POST_HEADER_BADGE_STYLE[selectedPost.status]}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    {translatePostStatus(selectedPost.status, t)}
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[selected.status]}`}>
                    {translateLinkedInStatus(selected.status, t)}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {translateCampaignType(selected.type, t)} · {t('campaigns.createdOn', { date: formatCampaignDisplayDate(selected.createdAt, localeTag) })}
              </p>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                <Eye className="h-3.5 w-3.5" /> {t('campaigns.preview')}
              </Button>
              <Button size="sm" className="gap-1.5 bg-blue-600 text-xs text-white hover:bg-blue-700">
                <Pencil className="h-3.5 w-3.5" /> {t('campaigns.edit')}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 border-b border-transparent">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {translateTab(tab, t)}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === 'Content' && (
            <>
              {/* Content Review Header */}
              <div className="flex shrink-0 items-end justify-between gap-1 border-b border-white/30 px-6 py-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{t('campaigns.contentReview')}</h3>
                  <p className="text-xs text-slate-500">{t('campaigns.contentReviewDesc')}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2.5">
                  <label
                    htmlFor="content-language"
                    className="shrink-0 whitespace-nowrap text-xs font-medium text-slate-600"
                  >
                    {t('common.language')}
                  </label>
                  <Select value={language} onValueChange={(v) => setLanguage(v as LanguageCode)}>
                    <SelectTrigger
                      id="content-language"
                      className="h-9 w-[11.5rem] gap-2 rounded-lg border border-white/45 bg-white/45 px-3 text-xs font-semibold text-slate-900 shadow-sm backdrop-blur-md focus-visible:ring-1 focus-visible:ring-blue-200 [&>svg:last-child]:ml-auto [&>svg:last-child]:text-slate-400"
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <LanguageFlagByCode code={language} />
                        <span className="truncate">{LANGUAGE_OPTIONS[language].label}</span>
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(LANGUAGE_OPTIONS) as LanguageCode[]).map((code) => (
                        <SelectItem key={code} value={code}>
                          <span className="flex items-center gap-2">
                            <LanguageFlagByCode code={code} />
                            <span>{LANGUAGE_OPTIONS[code].label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
              {/* Posts + Editor */}
              <div className="flex min-h-0 min-w-0 flex-1">
                {/* Posts List */}
                <div className={`flex min-h-0 w-[300px] shrink-0 flex-col overflow-hidden border-r border-white/35 ${CAMPAIGN_SURFACE_BG}`}>
                  <div className={`shrink-0 border-b border-white/35 px-4 py-3 ${LIQUID_GLASS_SOFT}`}>
                    <p className="text-xs font-semibold tracking-wide text-slate-700">{t('campaigns.posts')}</p>
                    <p className="text-[10px] text-slate-500">{t('campaigns.postsInCampaign', { count: selected.posts.length })}</p>
                  </div>
                  <div className={`min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain p-3 ${CAMPAIGN_SCROLLBAR}`}>
                    {selected.posts.map((post, index) => (
                      <ContentPostListItem
                        key={post.id}
                        post={post}
                        index={index}
                        isSelected={selectedPostId === post.id}
                        onSelect={() => setSelectedPostId(post.id)}
                      />
                    ))}
                  </div>
                  <div className="shrink-0 border-t border-white/35 p-3">
                    <button
                      type="button"
                      onClick={handleAddNewPost}
                      className={`flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-blue-300/60 bg-white/40 py-2.5 text-sm font-semibold text-blue-600 backdrop-blur-md transition-all hover:border-blue-400 hover:bg-white/70 hover:shadow-[0_6px_20px_-12px_rgba(59,130,246,0.3)] ${LIQUID_GLASS_SOFT}`}
                    >
                      <Plus className="h-4 w-4" /> {t('campaigns.addNewPost')}
                    </button>
                  </div>
                </div>

                {/* Post Editor */}
                {selectedPost ? (
                  <div key={selectedPost.id} className={`flex min-h-0 min-w-0 flex-1 flex-col px-6 ${CAMPAIGN_SURFACE_BG}`}>
                    <div className="flex h-full min-h-0 flex-col gap-5">
                      {/* Post Title */}
                      <div className="shrink-0">
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('campaigns.postTitle')}</label>
                        <div className="relative">
                          <input
                            type="text"
                            defaultValue={selectedPost.title}
                            className="w-full rounded-lg border border-white/45 bg-white/45 px-4 py-2.5 text-sm backdrop-blur-md focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{selectedPost.title.length} / 100</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex min-h-0 flex-1 flex-col">
                        <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('campaigns.content')}</label>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/45 bg-white/45 shadow-sm backdrop-blur-md focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-inset focus-within:ring-blue-200">
                          <textarea
                            defaultValue={`${selectedPost.content}\n\n${selectedPost.hashtags}`}
                            className="min-h-0 w-full flex-1 resize-none rounded-t-xl border-0 bg-transparent px-4 py-3 text-sm focus:outline-none"
                          />
                          {/* Toolbar */}
                          <div className="flex shrink-0 items-center justify-between rounded-b-xl border-t border-white/40 bg-white/30 px-3 py-2">
                            <div className="flex items-center gap-1">
                              {[Bold, Italic, Link2, List].map((Icon, i) => (
                                <button key={i} type="button" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                                  <Icon className="h-4 w-4" />
                                </button>
                              ))}
                              <span className="mx-1 h-4 w-px bg-slate-200" />
                              {[Image, Copy, Smile, Hash].map((Icon, i) => (
                                <button key={i} type="button" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                                  <Icon className="h-4 w-4" />
                                </button>
                              ))}
                            </div>
                            <span className="text-xs text-slate-400">{postContentLength} / 3000</span>
                          </div>
                        </div>
                      </div>

                      {/* Media | Call to Action | Actions */}
                      <div className="grid shrink-0 grid-cols-3 gap-5">
                        <div className="flex min-w-0 flex-col">
                          <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('campaigns.media')}</label>
                          <div className="mt-auto flex min-w-0 flex-col gap-2">
                            <span className="text-xs text-slate-500">
                              {mediaFiles.length === 0 ? t('campaigns.noImages') : t('campaigns.imageCount', { count: mediaFiles.length })}
                            </span>
                            <div
                              className={`flex max-w-full snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 [-webkit-overflow-scrolling:touch] ${CAMPAIGN_SCROLLBAR}`}
                              aria-label={t('campaigns.uploadedMedia')}
                            >
                              {mediaFiles.map((file, index) => (
                                <div
                                  key={`${file.name}-${file.size}-${file.lastModified}`}
                                  className="h-10 w-10 shrink-0 snap-start overflow-hidden rounded-lg border border-[#D9D9D9] bg-slate-100"
                                  title={file.name}
                                >
                                  <img
                                    src={mediaPreviewUrls[index]}
                                    alt={file.name}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => mediaInputRef.current?.click()}
                                className="flex h-10 w-10 shrink-0 snap-start items-center justify-center rounded-lg border-2 border-dashed border-[#D9D9D9] text-slate-400 hover:border-blue-300 hover:text-blue-500"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <input
                                ref={mediaInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleMediaChange}
                                className="hidden"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex min-w-0 flex-col">
                       
                          <div className="mt-auto">
                          <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('campaigns.callToAction')}</label>
                            <Select defaultValue="learn-more">
                              <SelectTrigger className="h-10 w-full border border-white/45 bg-white/45 text-sm backdrop-blur-md">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent side="top">
                                <SelectItem value="learn-more">{t('campaigns.cta.learnMore')}</SelectItem>
                                <SelectItem value="sign-up">{t('campaigns.cta.signUp')}</SelectItem>
                                <SelectItem value="contact">{t('campaigns.cta.contact')}</SelectItem>
                                <SelectItem value="download">{t('campaigns.cta.download')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex min-w-0 flex-col">
                          <label className="mb-1.5 block text-sm font-medium text-slate-700 opacity-0 select-none" aria-hidden>
                            Actions
                          </label>
                          <div className="mt-auto flex flex-wrap items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 border-white/45 bg-white/35 text-xs text-slate-900 backdrop-blur-md hover:bg-white/55"
                            >
                              {t('campaigns.saveDraft')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleApproveAndSchedule}
                              className="h-10 gap-1.5 bg-green-600 text-xs text-white hover:bg-green-700"
                            >
                              {t('campaigns.approveSchedule')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center ${CAMPAIGN_SURFACE_BG}`}>
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50">
                      <Pencil className="h-7 w-7 text-blue-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">{t('campaigns.noPostSelected')}</p>
                    <p className="text-xs text-slate-400">{t('campaigns.addFirstPost')}</p>
                  </div>
                )}
              </div>

              <div className="shrink-0">
                <CampaignContentSchedule
                  scheduleDays={scheduleDays}
                  calendarPosts={calendarPosts}
                  scheduleWindowStart={scheduleWindowStart}
                  scheduleRangeEnd={scheduleRangeEnd}
                  scheduleRangeLabel={scheduleRangeLabel}
                  onToday={goToToday}
                />
              </div>
              </div>
            </>
          )}

          {activeTab === 'Overview' && (
            <CampaignOverviewPanel onNavigateTab={setActiveTab} />
          )}

          {activeTab === 'Strategy' && (
            <CampaignStrategyPanel
              formProps={strategyFormProps}
              savedAt={strategySavedAt}
              isDirty={strategyIsDirty}
              hasErrors={hasStrategyValidationErrors(strategyErrors)}
              saveNotice={strategySaveNotice}
              isSaving={strategySaving}
              isGenerating={strategyAiLoading}
              onReset={handleResetStrategy}
              onSave={handleSaveStrategy}
              onGenerateAi={handleGenerateStrategyAi}
            />
          )}

          {activeTab === 'Schedule' && (
            <CampaignSchedulePanel
              campaignName={selected.name}
              posts={selected.posts}
              scheduleDays={scheduleDays}
              slots={campaignScheduleSlots}
              rangeLabel={scheduleRangeLabel}
              start={scheduleWindowStart}
              end={scheduleRangeEnd}
              timeZone={strategyDraft.timeZone}
              statusFilter={scheduleStatusFilter}
              showUnscheduled={showUnscheduledPanel}
              selectedPostId={selectedPostId}
              conflicts={scheduleConflicts}
              conflictCount={scheduleConflictCount}
              draggedPostId={draggedSchedulePostId}
              onToday={selectedId === 1 ? goToCampaignScheduleWeek : goToToday}
              onPrevWeek={() => shiftScheduleWeek(-1)}
              onNextWeek={() => shiftScheduleWeek(1)}
              onTimeZoneChange={(nextTimeZone) => patchStrategyDraft({ timeZone: nextTimeZone })}
              onStatusFilterChange={setScheduleStatusFilter}
              onShowUnscheduledChange={setShowUnscheduledPanel}
              onSelectPost={handleSelectSchedulePost}
              onDragStart={handleScheduleDragStart}
              onDragEnd={handleScheduleDragEnd}
              onDropHour={handleScheduleDrop}
            />
          )}

          {activeTab === 'Analytics' && <CampaignAnalyticsPanel />}

          {activeTab === 'Settings' && (
            <CampaignSettingsPanel
              campaignName={selected.name}
              language={strategyDraft.language}
              timeZone={strategyDraft.timeZone}
              isDirty={strategyIsDirty}
              saveNotice={strategySaveNotice}
              saving={strategySaving}
              onLanguageChange={(nextLanguage) => patchStrategyDraft({ language: nextLanguage })}
              onTimeZoneChange={(nextTimeZone) => patchStrategyDraft({ timeZone: nextTimeZone })}
              onSaveGeneral={handleSaveStrategy}
            />
          )}

          {activeTab !== 'Overview' && activeTab !== 'Content' && activeTab !== 'Strategy' && activeTab !== 'Schedule' && activeTab !== 'Analytics' && activeTab !== 'Settings' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
                <Calendar className="h-7 w-7 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">{translateTab(activeTab, t)}</p>
              <p className="text-xs text-slate-400">{t('campaigns.comingSoon')}</p>
            </div>
          )}

        </div>
      </div>

      {/* ── Right Panel: AI Assistant ── */}
      <div className={`flex w-[310px] shrink-0 flex-col overflow-hidden rounded-2xl ${LIQUID_GLASS_PANEL}`}>
        {activeTab === 'Schedule' ? (
          <CampaignScheduleSidePanel
            posts={selected.posts}
            slots={campaignScheduleSlots}
            statusFilter={scheduleStatusFilter}
            showUnscheduled={showUnscheduledPanel}
            selectedPostId={selectedPostId}
            conflicts={scheduleConflicts}
            onSelectPost={handleSelectSchedulePost}
            onDragStart={handleScheduleDragStart}
            onDragEnd={handleScheduleDragEnd}
            onUnscheduleDrop={handleUnscheduleDrop}
          />
        ) : (
          <>
        <div className="flex items-center justify-between border-b border-white/30 px-4 py-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">
              {activeTab === 'Strategy' ? t('campaigns.aiStrategyAssistant') : t('campaigns.aiContentAssistant')}
            </h3>
          </div>
          <button type="button" className="text-xs font-medium text-blue-600 hover:text-blue-700">{t('campaigns.newChat')}</button>
        </div>

        {activeTab === 'Strategy' ? (
          <StrategyAssistantPanel
            aiPreview={strategyAiPreview}
            isGenerating={strategyAiLoading}
            onGenerateAi={handleGenerateStrategyAi}
          />
        ) : (
          <>
        <div className={`flex-1 space-y-3 overflow-y-auto px-4 py-4 ${CAMPAIGN_SCROLLBAR}`}>
          {chatMessages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700'
                }`}
              >
                {msg.text.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < msg.text.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Type your message..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              className="flex-1 rounded-lg border border-white/45 bg-white/45 px-3 py-2 text-sm backdrop-blur-md focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
            />
            <button type="button" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
          </>
        )}
          </>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
